import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowRight,
  ChevronDown,
  Eye,
  Link2,
  Unlink2,
  Database,
  Italic,
  Check,
  MousePointer2,
  Plus,
  SquareMousePointer,
  Strikethrough,
  Underline,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ColorPicker } from "@/components/editor/ColorPicker";
import { Button } from "@/components/ui/button";
import { ConsistentTooltip } from "@/components/ui/ConsistentTooltip";
import { TextVariableInput as Input } from "@/components/ui/text-variable-input";
import { Label } from "@/components/ui/label";
import { VariableSelector } from "@/components/ui/variable-selector";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TextVariableTextarea as Textarea } from "@/components/ui/text-variable-textarea";
import { useVariablesStore } from "@/store/variablesStore";
import { toCSSVarReference } from "@/utils/cssVariables";
import type { CMSAction } from "@/services/cmsActionService";
import type { DataBinding } from "@/services/dataBindingService";
import { useEditorStore } from "@/store/editorStore";
import { useAuth } from "@/hooks/useAuth";
import { sanitizeSvgMarkup } from "@/utils/svgSanitizer";
import { useConvex } from "convex/react";
import { updateProject } from "@/data";
import { api } from "../../../convex/_generated/api";
import {
  ConditionalVisibilityDialog,
  type ConditionalVisibilityRule,
} from "./ConditionalVisibilityDialog";
import { DataBindingButton } from "./DataBindingButton";
import { CollectionItemsPanel, type CollectionItemsConfig } from "./CollectionItemsPanel";
import LucideIconSearch from "./LucideIconSearch";
import { VIEWPORT_PRESETS, type SkinBreakpointKey } from "./skinResponsive";
import { ActionsPanel } from "@/components/shared/ActionsPanel";

type SkinStylePreset = {
  id: string;
  name: string;
  componentType: SkinComponent["type"];
  style: Record<string, unknown>;
};

const SKIN_LOCAL_STYLE_ID = "local";

interface SkinComponent {
  id: string;
  type: "box" | "text" | "image" | "icon" | "collection-list" | "tabs" | "dropdown";
  name: string;
  position: {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
  };
  size: {
    width: number | string;
    height: number | string;
    minWidth?: number | string;
    minHeight?: number | string;
    maxWidth?: number | string;
    maxHeight?: number | string;
    aspectRatio?: string;
  };
  style: Record<string, unknown>;
  hoverStyle?: Record<string, unknown>;
  activeStyle?: Record<string, unknown>;
  visitedStyle?: Record<string, unknown>;
  margin?: { top: number; right: number; bottom: number; left: number };
  padding?: { top: number; right: number; bottom: number; left: number };
  content: Record<string, unknown>;
  visible: boolean;
  parentId?: string;
  zIndex: number;
  pointerEvents?: boolean;
  escToHide?: boolean;
  hideOnStart?: boolean;
  dataBindings?: Record<string, DataBinding>;
  cmsAction?: CMSAction;
}

interface ComponentPropertiesProps {
  selectedComponent: SkinComponent | null;
  onUpdateComponent: (componentId: string, updates: Partial<SkinComponent>) => void;
  onDuplicateComponent?: (component: SkinComponent) => void;
  onLinkPanorama?: (componentId: string, panoramaId: string) => void;
  components?: SkinComponent[];
  panoramasFromProps?: Array<Record<string, unknown>>;
  currentPanoramaId?: string;
}

export const ComponentProperties = ({
  selectedComponent,
  onUpdateComponent,
  onDuplicateComponent: _onDuplicateComponent,
  onLinkPanorama: _onLinkPanorama,
  components: _components = [],
  panoramasFromProps: _panoramasFromProps = [],
  currentPanoramaId: _currentPanoramaId,
}: ComponentPropertiesProps) => {
  const [activeTab, setActiveTab] = useState("style");
  const [localWidth, setLocalWidth] = useState<string>("auto");
  const [localHeight, setLocalHeight] = useState<string>("auto");
  const [localMinWidth, setLocalMinWidth] = useState<string>("");
  const [localMinHeight, setLocalMinHeight] = useState<string>("");
  const [localMaxWidth, setLocalMaxWidth] = useState<string>("");
  const [localMaxHeight, setLocalMaxHeight] = useState<string>("");
  const [localLeft, setLocalLeft] = useState<string>("");
  const [localTop, setLocalTop] = useState<string>("");
  const [localRight, setLocalRight] = useState<string>("");
  const [localBottom, setLocalBottom] = useState<string>("");
  const [localBorderRadius, setLocalBorderRadius] = useState<string>("");
  const [localAspectRatio, setLocalAspectRatio] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [localSvgMarkup, setLocalSvgMarkup] = useState<string>("");
  const [skinBreakpointKey, setSkinBreakpointKey] = useState<
    "base" | SkinBreakpointKey
  >("base");
  const [skinStyleTab, setSkinStyleTab] = useState<"base" | "hover" | "active" | "visited">("base");
  const fontUploadInputRef = useRef<HTMLInputElement | null>(null);
  const skinStylePresetInputRef = useRef<HTMLInputElement | null>(null);
  const [skinStylePresets, setSkinStylePresets] = useState<SkinStylePreset[]>([]);
  const [skinStylePresetInputValue, setSkinStylePresetInputValue] = useState("");
  const [selectedSkinStylePresetId, setSelectedSkinStylePresetId] =
    useState<string>(SKIN_LOCAL_STYLE_ID);
  const [isSkinStyleDropdownOpen, setIsSkinStyleDropdownOpen] = useState(false);

  const [expandedActionIds, setExpandedActionIds] = useState<Set<string>>(new Set());
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [hotspotVisibilityConfigs, setHotspotVisibilityConfigs] = useState<Record<string, CollectionItemsConfig>>({});
  const [showConditionalVisibilityDialog, setShowConditionalVisibilityDialog] = useState(false);
  const showConditionalVisibility = false;
  const [_collectionName, setCollectionName] = useState<string>("");
  const [collectionFields, setCollectionFields] = useState<Array<Record<string, unknown>>>([]);
  const [availableCollections, setAvailableCollections] = useState<Array<Record<string, unknown>>>(
    [],
  );
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [collectionItems, setCollectionItems] = useState<Array<Record<string, unknown>>>([]);

  // Get store data
  const convex = useConvex();
  const { panoramas, hotspots, skinComponents, resolvedBindings, project, projectBackend, setProject } =
    useEditorStore();
  const { isAuthenticated } = useAuth();
  const { getProjectVariables, getCraftspaceVariables, addFontFamilyVariable } = useVariablesStore();

  const styleKey =
    skinStyleTab === "base"
      ? "style"
      : skinStyleTab === "hover"
        ? "hoverStyle"
        : skinStyleTab === "active"
          ? "activeStyle"
          : "visitedStyle";
  const baseStyle =
    selectedComponent && selectedComponent[styleKey] && typeof selectedComponent[styleKey] === "object"
      ? (selectedComponent[styleKey] as Record<string, unknown>)
      : {};

  const projectFontFamilies = project?.id
    ? getProjectVariables(project.id).fontFamilies
    : [];
  const craftspaceFontFamilies = project?.craftspace_id
    ? getCraftspaceVariables(project.craftspace_id).fontFamilies.filter(
        (wsFont) => !projectFontFamilies.some((projFont) => projFont.name === wsFont.name),
      )
    : [];

  const skinStylePresetStorageKey = useMemo(
    () => `skinStylePresets:${project?.id || "global"}`,
    [project?.id],
  );

  useEffect(() => {
    if (!selectedComponent) return;
    const presetId = (selectedComponent.content?.skinStylePresetId as string) || SKIN_LOCAL_STYLE_ID;
    setSelectedSkinStylePresetId(presetId);
    setSkinStylePresetInputValue("");
  }, [selectedComponent?.id]);

  const normalizeStyleName = (name: string) =>
    name.trim().toLowerCase().replace(/\s+/g, "-");

  const persistSkinStylePresets = async (presets: SkinStylePreset[]) => {
    setSkinStylePresets(presets);
    if (!project) return;
    const currentData =
      (project.data as Record<string, unknown> | null | undefined) ?? {};
    const nextData = { ...currentData, skinStylePresets: presets };
    if (projectBackend === "local") {
      await updateProject({
        id: project.id,
        craftspace_id: project.craftspace_id || "default",
        data: nextData,
      });
    } else {
      if (!isAuthenticated) return;
      await convex.mutation(api.projects.update, { id: project.id, data: nextData });
    }
    setProject({ ...project, data: nextData });
  };

  useEffect(() => {
    const projectPresets = Array.isArray(
      (project?.data as Record<string, unknown> | null | undefined)?.skinStylePresets,
    )
      ? ((project?.data as Record<string, unknown>).skinStylePresets as SkinStylePreset[])
      : null;

    if (projectPresets) {
      setSkinStylePresets(projectPresets);
      setSkinStylePresetInputValue("");
      const hasSelected = projectPresets.some((preset) => preset.id === selectedSkinStylePresetId);
      if (!hasSelected && selectedSkinStylePresetId !== SKIN_LOCAL_STYLE_ID) {
        setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
      }
      return;
    }

    const raw = localStorage.getItem(skinStylePresetStorageKey);
    if (!raw) {
      setSkinStylePresets([]);
      setSkinStylePresetInputValue("");
      setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const sanitized = parsed.filter(
          (item): item is SkinStylePreset =>
            item &&
            typeof item.id === "string" &&
            typeof item.name === "string" &&
            typeof item.componentType === "string" &&
            typeof item.style === "object",
        );
        setSkinStylePresets(sanitized);
        setSkinStylePresetInputValue("");
        setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
        void persistSkinStylePresets(sanitized);
        localStorage.removeItem(skinStylePresetStorageKey);
        return;
      }
    } catch {
      // ignore malformed local storage data
    }
    setSkinStylePresets([]);
    setSkinStylePresetInputValue("");
    setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
  }, [project?.data, skinStylePresetStorageKey, selectedSkinStylePresetId]);

  const handleFontUpload = (file: File) => {
    if (!project?.id || !selectedComponent) return;
    const baseName = file.name.replace(/\.[^/.]+$/, "") || "custom-font";
    const normalizedBase = baseName.toLowerCase().replace(/[^a-z0-9-]/g, "-") || "custom-font";
    const existingNames = new Set(
      [...projectFontFamilies, ...craftspaceFontFamilies].map((font) => font.name),
    );
    let name = normalizedBase;
    let index = 1;
    while (existingNames.has(name)) {
      index += 1;
      name = `${normalizedBase}-${index}`;
    }
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const format =
      extension === "woff2"
        ? "woff2"
        : extension === "woff"
          ? "woff"
          : extension === "ttf"
            ? "truetype"
            : extension === "otf"
              ? "opentype"
              : "woff2";
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      addFontFamilyVariable(project.craftspace_id ?? "", project.id, {
        name,
        value: `"${baseName}"`,
        src: dataUrl,
        format,
      });
      handleUpdate({
        style: { ...resolvedStyle, fontFamily: toCSSVarReference(name) },
      });
    };
    reader.readAsDataURL(file);
  };

  // Check if component is part of a Collection
  const isPartOfCollectionList = selectedComponent?.parentId
    ? skinComponents.find((c) => c.id === selectedComponent.parentId)?.type === "collection-list"
    : false;

  const compatibleSkinStylePresets = useMemo(() => {
    if (!selectedComponent) return [];
    return skinStylePresets.filter((preset) => preset.componentType === selectedComponent.type);
  }, [selectedComponent, skinStylePresets]);
  const presetStyle =
    selectedSkinStylePresetId !== SKIN_LOCAL_STYLE_ID
      ? compatibleSkinStylePresets.find((preset) => preset.id === selectedSkinStylePresetId)?.style
      : null;
  const resolvedStyle = presetStyle ? { ...presetStyle, ...baseStyle } : baseStyle;

  const normalizedSkinStyleInput = normalizeStyleName(skinStylePresetInputValue);
  const matchingSkinPresetByInput = normalizedSkinStyleInput
    ? compatibleSkinStylePresets.find(
        (preset) => normalizeStyleName(preset.name) === normalizedSkinStyleInput,
      )
    : null;
  const filteredSkinStylePresets = normalizedSkinStyleInput
    ? compatibleSkinStylePresets.filter((preset) =>
        normalizeStyleName(preset.name).includes(normalizedSkinStyleInput),
      )
    : compatibleSkinStylePresets;

  const selectedSkinStylePreset =
    selectedSkinStylePresetId === SKIN_LOCAL_STYLE_ID
      ? null
      : compatibleSkinStylePresets.find((preset) => preset.id === selectedSkinStylePresetId) ||
        null;

  // Debug logging
  // console.log("🔍 ComponentProperties Debug:", {
  //   selectedComponentId: selectedComponent?.id,
  //   selectedComponentType: selectedComponent?.type,
  //   parentId: selectedComponent?.parentId,
  //   isPartOfCollectionList,
  //   parentComponent: selectedComponent?.parentId
  //     ? skinComponents.find((c) => c.id === selectedComponent.parentId)
  //     : null,
  // });

  // Conditional visibility state
  const [conditionalVisibilityRules, setConditionalVisibilityRules] = useState<
    ConditionalVisibilityRule[]
  >((selectedComponent?.content as Record<string, unknown>)?.conditionalVisibilityRules || []);
  const [dropdownFields, setDropdownFields] = useState<Array<Record<string, unknown>>>([]);

  // Load collections when component changes
  useEffect(() => {
    const loadCollections = async () => {
      if (selectedComponent && project?.id) {
        try {
          setIsLoadingCollections(true);
          const { CMSService } = await import("@/services/cmsService");
          const collections = await CMSService.loadProjectCMSData(project.id);
          setAvailableCollections(collections);

          // Load current collection data if component has a data source
          let dataSourceId = null;

          // If this is a collection-list component, get its dataSourceId
          if (
            selectedComponent.type === "collection-list" &&
            selectedComponent.content?.dataSourceId
          ) {
            dataSourceId = selectedComponent.content.dataSourceId;
          }
          // If this is a child component, find its parent collection-list
          else if (selectedComponent.parentId) {
            const parentComponent = skinComponents.find((c) => c.id === selectedComponent.parentId);
            if (
              parentComponent?.type === "collection-list" &&
              parentComponent.content?.dataSourceId
            ) {
              dataSourceId = parentComponent.content.dataSourceId;
            }
          }

          if (dataSourceId) {
            const targetCollection = collections.find((c) => c.id === dataSourceId);
            setCollectionName(targetCollection?.name || "Unknown Collection");
            setCollectionFields(targetCollection?.fieldStructure || []);

            // Load collection items for the Selected mode
            if (project?.craftspace_id) {
              try {
                const { CMSService } = await import("@/services/cmsService");
                const datasetWithContent = await CMSService.loadCMSDataWithContent(
                  dataSourceId,
                  project.craftspace_id,
                );
                setCollectionItems(datasetWithContent.data?.rows || []);
              } catch {
                setCollectionItems([]);
              }
            }
          } else {
            setCollectionName("");
            setCollectionFields([]);
            setCollectionItems([]);
          }
        } catch (error) {
          console.error("Error loading collections:", error);
          setCollectionName("Error loading collections");
          setCollectionFields([]);
          setCollectionItems([]);
        } finally {
          setIsLoadingCollections(false);
        }
      } else {
        setCollectionName("");
        setCollectionFields([]);
        setAvailableCollections([]);
        setCollectionItems([]);
      }
    };

    loadCollections();
  }, [selectedComponent, project?.id, skinComponents, isAuthenticated]);

  useEffect(() => {
    const loadDropdownFields = async () => {
      if (
        selectedComponent?.type !== "dropdown" ||
        !selectedComponent.content?.dataSourceId ||
        !project?.craftspace_id
      ) {
        setDropdownFields([]);
        return;
      }
      try {
        const fields = await DataBindingService.getDatasetFields(
          selectedComponent.content.dataSourceId as string,
          project.craftspace_id,
        );
        setDropdownFields(fields as Array<Record<string, unknown>>);
      } catch (error) {
        console.error("Error loading dropdown fields:", error);
        setDropdownFields([]);
      }
    };
    void loadDropdownFields();
  }, [selectedComponent?.id, selectedComponent?.content?.dataSourceId, project?.craftspace_id]);

  // Handle collection source selection
  const handleCollectionSourceSelected = (sourceId: string) => {
    if (selectedComponent) {
      onUpdateComponent(selectedComponent.id, {
        content: {
          ...selectedComponent.content,
          dataSourceId: sourceId,
        },
      });
    }
  };

  // Handle collection selection from dropdown
  const handleCollectionChange = (value: string) => {
    if (value === "none") {
      // Clear data source
      onUpdateComponent(selectedComponent.id, {
        content: {
          ...selectedComponent.content,
          dataSourceId: undefined,
        },
      });
    } else {
      handleCollectionSourceSelected(value);
    }
  };

  // Content and action state
  const content = (selectedComponent?.content as Record<string, unknown>) || {};

  useEffect(() => {
    if (!selectedComponent) return;
    const presetId = content.skinStylePresetId;
    if (typeof presetId === "string") {
      setSelectedSkinStylePresetId(presetId);
    } else {
      setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
    }
  }, [selectedComponent?.id, content.skinStylePresetId]);
  const hasConditionalVisibility = !!(
    conditionalVisibilityRules && conditionalVisibilityRules.length > 0
  );
  const tabStyle = (content.tabStyle as Record<string, unknown>) || {};
  const tabsList = Array.isArray(content.tabs)
    ? (content.tabs as Array<{ id: string; label: string }>)
    : [];
  // Data binding state for text content
  const textContentBinding = selectedComponent?.dataBindings?.["content.text"] || null;

  // Helper function to get resolved text content for input field
  const getResolvedTextContent = (): string => {
    if (!selectedComponent) return "";

    const propertyPath = "content.text";
    const binding = selectedComponent.dataBindings?.[propertyPath];

    // Check if there's a resolved binding value
    if (binding && resolvedBindings[selectedComponent.id]?.[propertyPath]) {
      return resolvedBindings[selectedComponent.id][propertyPath];
    }

    // If there's a binding but no resolved data yet, show a loading message
    if (binding) {
      return "Loading CMS data...";
    }

    // For input field, return the actual content (empty string if no content)
    // Don't show "Text" placeholder in input field - that's only for canvas
    return selectedComponent.content?.text ?? "";
  };

  // Data binding state for image source
  const imageSourceBinding = selectedComponent?.dataBindings?.["content.src"] || null;

  // Data binding state for visibility
  const visibilityBinding = selectedComponent?.dataBindings?.visible || null;

  const handleLucideIconSelect = (iconName: string, _svgCode: string) => {
    handleUpdate({
      content: {
        ...selectedComponent?.content,
        // Prefer storing just the Lucide icon name; renderer will draw it
        svg: "",
        lucideIcon: iconName,
        src: "",
      },
    });
  };

  // Handle data binding changes for text content
  const handleTextContentBindingChange = (binding: DataBinding | null) => {
    if (!selectedComponent) return;

    const currentDataBindings = selectedComponent.dataBindings || {};

    if (binding) {
      // Add or update the binding
      handleUpdate({
        dataBindings: {
          ...currentDataBindings,
          "content.text": binding,
        },
      });
    } else {
      // Remove the binding
      const newDataBindings = { ...currentDataBindings };
      delete newDataBindings["content.text"];
      handleUpdate({
        dataBindings: newDataBindings,
      });
    }
  };

  // Handle data binding changes for image source
  const handleImageSourceBindingChange = (binding: DataBinding | null) => {
    if (!selectedComponent) return;

    const currentDataBindings = selectedComponent.dataBindings || {};

    if (binding) {
      // Add or update the binding
      handleUpdate({
        dataBindings: {
          ...currentDataBindings,
          "content.src": binding,
        },
      });
    } else {
      // Remove the binding
      const newDataBindings = { ...currentDataBindings };
      delete newDataBindings["content.src"];
      handleUpdate({
        dataBindings: newDataBindings,
      });
    }
  };

  // Handle data binding changes for visibility
  const handleVisibilityBindingChange = (binding: DataBinding | null) => {
    if (!selectedComponent) return;

    const currentDataBindings = selectedComponent.dataBindings || {};

    if (binding) {
      // Add or update the binding
      handleUpdate({
        dataBindings: {
          ...currentDataBindings,
          visible: binding,
        },
      });
    } else {
      // Remove the binding
      const newDataBindings = { ...currentDataBindings };
      delete newDataBindings.visible;
      handleUpdate({
        dataBindings: newDataBindings,
      });
    }
  };

  // Handle conditional visibility rules
  const handleConditionalVisibilityRulesChange = (rules: ConditionalVisibilityRule[]) => {
    setConditionalVisibilityRules(rules);
    handleUpdate({
      content: {
        ...content,
        conditionalVisibilityRules: rules,
      },
    });
  };

  const handleRemoveConditionalVisibility = () => {
    setConditionalVisibilityRules([]);
    handleUpdate({
      content: {
        ...content,
        conditionalVisibilityRules: undefined,
      },
    });
  };

  // Reset filename when component changes or when image source is cleared
  useEffect(() => {
    if (selectedComponent) {
      if (
        !selectedComponent.content?.src ||
        (selectedComponent.type !== "image" && selectedComponent.type !== "icon")
      ) {
        setSelectedFileName("");
      }
    } else {
      setSelectedFileName("");
    }
  }, [selectedComponent]);

  useEffect(() => {
    const currentSvg =
      selectedComponent?.content && typeof selectedComponent.content.svg === "string"
        ? selectedComponent.content.svg
        : "";
    setLocalSvgMarkup(currentSvg);
  }, [selectedComponent?.content]);

  const [dropdownOptionsText, setDropdownOptionsText] = useState<string>("");

  useEffect(() => {
    if (selectedComponent?.type === "dropdown") {
      const options = Array.isArray(selectedComponent.content?.options)
        ? (selectedComponent.content.options as string[])
        : [];
      setDropdownOptionsText(options.join("\n"));
    }
  }, [selectedComponent?.id]);

  // Padding-related state
  const [localPadding, setLocalPadding] = useState<string>("");
  const [showPaddingFields, setShowPaddingFields] = useState<boolean>(false);
  const [localPaddingTop, setLocalPaddingTop] = useState<string>("");
  const [localPaddingRight, setLocalPaddingRight] = useState<string>("");
  const [localPaddingBottom, setLocalPaddingBottom] = useState<string>("");
  const [localPaddingLeft, setLocalPaddingLeft] = useState<string>("");
  // Margin-related state
  const [localMargin, setLocalMargin] = useState<string>("");
  const [showMarginFields, setShowMarginFields] = useState<boolean>(false);
  const [localMarginTop, setLocalMarginTop] = useState<string>("");
  const [localMarginRight, setLocalMarginRight] = useState<string>("");
  const [localMarginBottom, setLocalMarginBottom] = useState<string>("");
  const [localMarginLeft, setLocalMarginLeft] = useState<string>("");

  // Border-related state
  const [localBorderStyle, setLocalBorderStyle] = useState<string>("solid");
  const [isDefaultBorderStyle, setIsDefaultBorderStyle] = useState<boolean>(true);
  const [localBorderWidth, setLocalBorderWidth] = useState<string>("1");
  const [_localBorderColor, setLocalBorderColor] = useState<string>("#000000");
  const [showBorderWidthFields, setShowBorderWidthFields] = useState<boolean>(false);
  const [showBorderRadiusFields, setShowBorderRadiusFields] = useState<boolean>(false);

  // Individual border width states
  const [localBorderTopWidth, setLocalBorderTopWidth] = useState<string>("");
  const [localBorderRightWidth, setLocalBorderRightWidth] = useState<string>("");
  const [localBorderBottomWidth, setLocalBorderBottomWidth] = useState<string>("");
  const [localBorderLeftWidth, setLocalBorderLeftWidth] = useState<string>("");

  // Individual border radius states
  const [localBorderTopLeftRadius, setLocalBorderTopLeftRadius] = useState<string>("");
  const [localBorderTopRightRadius, setLocalBorderTopRightRadius] = useState<string>("");
  const [localBorderBottomLeftRadius, setLocalBorderBottomLeftRadius] = useState<string>("");
  const [localBorderBottomRightRadius, setLocalBorderBottomRightRadius] = useState<string>("");

  // Track current units for each field to maintain consistency
  const [widthUnit, setWidthUnit] = useState<"px" | "%">("%");
  const [heightUnit, setHeightUnit] = useState<"px" | "%">("%");
  const [minWidthUnit, setMinWidthUnit] = useState<"px" | "%">("%");
  const [minHeightUnit, setMinHeightUnit] = useState<"px" | "%">("%");
  const [maxWidthUnit, setMaxWidthUnit] = useState<"px" | "%">("%");
  const [maxHeightUnit, setMaxHeightUnit] = useState<"px" | "%">("%");
  const [leftUnit, setLeftUnit] = useState<"px" | "%">("%");
  const [topUnit, setTopUnit] = useState<"px" | "%">("%");
  const [rightUnit, setRightUnit] = useState<"px" | "%">("%");
  const [bottomUnit, setBottomUnit] = useState<"px" | "%">("%");

  // Padding unit states
  const [paddingUnit, setPaddingUnit] = useState<"px" | "%">("px");
  const [paddingTopUnit, setPaddingTopUnit] = useState<"px" | "%">("px");
  const [_paddingRightUnit, setPaddingRightUnit] = useState<"px" | "%">("px");
  const [_paddingBottomUnit, setPaddingBottomUnit] = useState<"px" | "%">("px");
  const [_paddingLeftUnit, setPaddingLeftUnit] = useState<"px" | "%">("px");

  // Border width unit states
  const [_borderWidthUnit, setBorderWidthUnit] = useState<"px" | "%">("px");
  const [_borderTopWidthUnit, setBorderTopWidthUnit] = useState<"px" | "%">("px");
  const [_borderRightWidthUnit, setBorderRightWidthUnit] = useState<"px" | "%">("px");
  const [_borderBottomWidthUnit, setBorderBottomWidthUnit] = useState<"px" | "%">("px");
  const [_borderLeftWidthUnit, setBorderLeftWidthUnit] = useState<"px" | "%">("px");

  // Border radius unit states
  const [_borderRadiusUnit, setBorderRadiusUnit] = useState<"px" | "%">("px");
  const [_borderTopLeftRadiusUnit, setBorderTopLeftRadiusUnit] = useState<"px" | "%">("px");
  const [_borderTopRightRadiusUnit, setBorderTopRightRadiusUnit] = useState<"px" | "%">("px");
  const [_borderBottomLeftRadiusUnit, setBorderBottomLeftRadiusUnit] = useState<"px" | "%">("px");
  const [_borderBottomRightRadiusUnit, setBorderBottomRightRadiusUnit] = useState<"px" | "%">("px");

  // Box gap states
  const [gapLinked, setGapLinked] = useState(true);

  // Backdrop filter states
  const [localBackdropFilterType, setLocalBackdropFilterType] = useState<string>("blur");
  const [localBackdropFilterValue, setLocalBackdropFilterValue] = useState<string>("0");
  const [backdropFilterUnit, setBackdropFilterUnit] = useState<"px" | "%" | "deg" | "">("px");
  const [originalBackdropFilterValue, setOriginalBackdropFilterValue] = useState<string>("0");
  const [originalBackdropFilterUnit, setOriginalBackdropFilterUnit] = useState<
    "px" | "%" | "deg" | ""
  >("px");

  const widthInputRef = useRef<HTMLInputElement>(null);
  const heightInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get filter type info
  const getFilterTypeInfo = (filterType: string) => {
    switch (filterType) {
      case "blur":
        return { unit: "px", defaultValue: "0", min: "0", max: "100", step: "1" };
      case "brightness":
        return { unit: "%", defaultValue: "100", min: "0", max: "300", step: "1" };
      case "contrast":
        return { unit: "%", defaultValue: "100", min: "0", max: "300", step: "1" };

      case "grayscale":
        return { unit: "%", defaultValue: "0", min: "0", max: "100", step: "1" };
      case "hue-rotate":
        return { unit: "deg", defaultValue: "0", min: "0", max: "360", step: "1" };
      case "invert":
        return { unit: "%", defaultValue: "0", min: "0", max: "100", step: "1" };

      case "saturate":
        return { unit: "%", defaultValue: "100", min: "0", max: "300", step: "1" };
      case "sepia":
        return { unit: "%", defaultValue: "0", min: "0", max: "100", step: "1" };
      default:
        return { unit: "px", defaultValue: "0", min: "0", max: "100", step: "1" };
    }
  };

  // Helper function to get filter description
  const getFilterDescription = (filterType: string) => {
    switch (filterType) {
      case "blur":
        return "Creates a soft, out-of-focus effect. Higher values create more blur.";
      case "brightness":
        return "Adjusts the lightness. 100% is normal, 0% is black, 200% is twice as bright.";
      case "contrast":
        return "Controls the difference between light and dark areas. 100% is normal, 0% is gray.";

      case "grayscale":
        return "Converts colors to grayscale. 0% is full color, 100% is completely grayscale.";
      case "hue-rotate":
        return "Shifts all colors by the specified angle. 0° is normal, 180° inverts colors.";
      case "invert":
        return "Inverts the colors. 0% is normal, 100% is completely inverted.";

      case "saturate":
        return "Adjusts color intensity. 100% is normal, 0% removes all color, 200% doubles intensity.";
      case "sepia":
        return "Applies a vintage brown tone. 0% is normal, 100% is full sepia effect.";
      default:
        return "";
    }
  };

  // Sync local state with selected component
  // Sync local state with selected component
  useEffect(() => {
    if (selectedComponent) {
      // Add a small delay to ensure the component has fully rendered
      const timer = setTimeout(() => {
        // Handle both numeric and string values (like 'auto') - strip units for display
        const newWidth =
          typeof selectedComponent.size.width === "string" &&
          selectedComponent.size.width.endsWith("%")
            ? selectedComponent.size.width.replace("%", "")
            : selectedComponent.size.width.toString();

        const newHeight =
          typeof selectedComponent.size.height === "string" &&
          selectedComponent.size.height.endsWith("%")
            ? selectedComponent.size.height.replace("%", "")
            : selectedComponent.size.height.toString();

        // Handle min size values - strip units for display
        const newMinWidth =
          selectedComponent.size.minWidth !== undefined
            ? typeof selectedComponent.size.minWidth === "string" &&
              selectedComponent.size.minWidth.endsWith("%")
              ? selectedComponent.size.minWidth.replace("%", "")
              : selectedComponent.size.minWidth.toString()
            : "";
        const newMinHeight =
          selectedComponent.size.minHeight !== undefined
            ? typeof selectedComponent.size.minHeight === "string" &&
              selectedComponent.size.minHeight.endsWith("%")
              ? selectedComponent.size.minHeight.replace("%", "")
              : selectedComponent.size.minHeight.toString()
            : "";

        // Handle max size values - strip units for display
        const newMaxWidth =
          selectedComponent.size.maxWidth !== undefined
            ? typeof selectedComponent.size.maxWidth === "string" &&
              selectedComponent.size.maxWidth.endsWith("%")
              ? selectedComponent.size.maxWidth.replace("%", "")
              : selectedComponent.size.maxWidth.toString()
            : "";
        const newMaxHeight =
          selectedComponent.size.maxHeight !== undefined
            ? typeof selectedComponent.size.maxHeight === "string" &&
              selectedComponent.size.maxHeight.endsWith("%")
              ? selectedComponent.size.maxHeight.replace("%", "")
              : selectedComponent.size.maxHeight.toString()
            : "";

        // Handle position values - strip units for display
        const newLeft =
          selectedComponent.position.left !== undefined
            ? typeof selectedComponent.position.left === "string" &&
              selectedComponent.position.left.endsWith("%")
              ? selectedComponent.position.left.replace("%", "")
              : selectedComponent.position.left.toString()
            : "";
        const newTop =
          selectedComponent.position.top !== undefined
            ? typeof selectedComponent.position.top === "string" &&
              selectedComponent.position.top.endsWith("%")
              ? selectedComponent.position.top.replace("%", "")
              : selectedComponent.position.top.toString()
            : "";
        const newRight =
          selectedComponent.position.right !== undefined
            ? typeof selectedComponent.position.right === "string" &&
              selectedComponent.position.right.endsWith("%")
              ? selectedComponent.position.right.replace("%", "")
              : selectedComponent.position.right.toString()
            : "";
        const newBottom =
          selectedComponent.position.bottom !== undefined
            ? typeof selectedComponent.position.bottom === "string" &&
              selectedComponent.position.bottom.endsWith("%")
              ? selectedComponent.position.bottom.replace("%", "")
              : selectedComponent.position.bottom.toString()
            : "";

        // Set units based on current component values
        if (
          typeof selectedComponent.size.width === "string" &&
          selectedComponent.size.width.endsWith("%")
        ) {
          setWidthUnit("%");
        } else if (selectedComponent.size.width === "auto") {
          setWidthUnit("%"); // Default to percentage for auto values
        } else if (typeof selectedComponent.size.width === "number") {
          setWidthUnit("px");
        }

        if (
          typeof selectedComponent.size.height === "string" &&
          selectedComponent.size.height.endsWith("%")
        ) {
          setHeightUnit("%");
        } else if (selectedComponent.size.height === "auto") {
          setHeightUnit("%"); // Default to percentage for auto values
        } else if (typeof selectedComponent.size.height === "number") {
          setHeightUnit("px");
        }

        if (
          typeof selectedComponent.size.minWidth === "string" &&
          selectedComponent.size.minWidth.endsWith("%")
        ) {
          setMinWidthUnit("%");
        } else if (typeof selectedComponent.size.minWidth === "number") {
          setMinWidthUnit("px");
        }

        if (
          typeof selectedComponent.size.minHeight === "string" &&
          selectedComponent.size.minHeight.endsWith("%")
        ) {
          setMinHeightUnit("%");
        } else if (typeof selectedComponent.size.minHeight === "number") {
          setMinHeightUnit("px");
        }

        if (
          typeof selectedComponent.size.maxWidth === "string" &&
          selectedComponent.size.maxWidth.endsWith("%")
        ) {
          setMaxWidthUnit("%");
        } else if (typeof selectedComponent.size.maxWidth === "number") {
          setMaxWidthUnit("px");
        }

        if (
          typeof selectedComponent.size.maxHeight === "string" &&
          selectedComponent.size.maxHeight.endsWith("%")
        ) {
          setMaxHeightUnit("%");
        } else if (typeof selectedComponent.size.maxHeight === "number") {
          setMaxHeightUnit("px");
        }

        if (
          typeof selectedComponent.position.left === "string" &&
          selectedComponent.position.left.endsWith("%")
        ) {
          setLeftUnit("%");
        } else if (typeof selectedComponent.position.left === "number") {
          setLeftUnit("px");
        }

        if (
          typeof selectedComponent.position.top === "string" &&
          selectedComponent.position.top.endsWith("%")
        ) {
          setTopUnit("%");
        } else if (typeof selectedComponent.position.top === "number") {
          setTopUnit("px");
        }

        if (
          typeof selectedComponent.position.right === "string" &&
          selectedComponent.position.right.endsWith("%")
        ) {
          setRightUnit("%");
        } else if (typeof selectedComponent.position.right === "number") {
          setRightUnit("px");
        }

        if (
          typeof selectedComponent.position.bottom === "string" &&
          selectedComponent.position.bottom.endsWith("%")
        ) {
          setBottomUnit("%");
        } else if (typeof selectedComponent.position.bottom === "number") {
          setBottomUnit("px");
        }

        // Initialize padding units (default to px for padding)
        if (
          typeof resolvedStyle.padding === "string" &&
          resolvedStyle.padding.endsWith("%")
        ) {
          setPaddingUnit("%");
        } else {
          setPaddingUnit("px");
        }

        if (
          typeof resolvedStyle.paddingTop === "string" &&
          resolvedStyle.paddingTop.endsWith("%")
        ) {
          setPaddingTopUnit("%");
        } else {
          setPaddingTopUnit("px");
        }

        if (
          typeof resolvedStyle.paddingRight === "string" &&
          resolvedStyle.paddingRight.endsWith("%")
        ) {
          setPaddingRightUnit("%");
        } else {
          setPaddingRightUnit("px");
        }

        if (
          typeof resolvedStyle.paddingBottom === "string" &&
          resolvedStyle.paddingBottom.endsWith("%")
        ) {
          setPaddingBottomUnit("%");
        } else {
          setPaddingBottomUnit("px");
        }

        if (
          typeof resolvedStyle.paddingLeft === "string" &&
          resolvedStyle.paddingLeft.endsWith("%")
        ) {
          setPaddingLeftUnit("%");
        } else {
          setPaddingLeftUnit("px");
        }

        // Initialize border width units (default to px for borders)
        if (
          typeof resolvedStyle.borderWidth === "string" &&
          resolvedStyle.borderWidth.endsWith("%")
        ) {
          setBorderWidthUnit("%");
        } else {
          setBorderWidthUnit("px");
        }

        if (
          typeof resolvedStyle.borderTopWidth === "string" &&
          resolvedStyle.borderTopWidth.endsWith("%")
        ) {
          setBorderTopWidthUnit("%");
        } else {
          setBorderTopWidthUnit("px");
        }

        if (
          typeof resolvedStyle.borderRightWidth === "string" &&
          resolvedStyle.borderRightWidth.endsWith("%")
        ) {
          setBorderRightWidthUnit("%");
        } else {
          setBorderRightWidthUnit("px");
        }

        if (
          typeof resolvedStyle.borderBottomWidth === "string" &&
          resolvedStyle.borderBottomWidth.endsWith("%")
        ) {
          setBorderBottomWidthUnit("%");
        } else {
          setBorderBottomWidthUnit("px");
        }

        if (
          typeof resolvedStyle.borderLeftWidth === "string" &&
          resolvedStyle.borderLeftWidth.endsWith("%")
        ) {
          setBorderLeftWidthUnit("%");
        } else {
          setBorderLeftWidthUnit("px");
        }

        // Initialize border radius units (default to px for border radius)
        if (
          typeof resolvedStyle.borderRadius === "string" &&
          resolvedStyle.borderRadius.endsWith("%")
        ) {
          setBorderRadiusUnit("%");
        } else {
          setBorderRadiusUnit("px");
        }

        if (
          typeof resolvedStyle.borderTopLeftRadius === "string" &&
          resolvedStyle.borderTopLeftRadius.endsWith("%")
        ) {
          setBorderTopLeftRadiusUnit("%");
        } else {
          setBorderTopLeftRadiusUnit("px");
        }

        if (
          typeof resolvedStyle.borderTopRightRadius === "string" &&
          resolvedStyle.borderTopRightRadius.endsWith("%")
        ) {
          setBorderTopRightRadiusUnit("%");
        } else {
          setBorderTopRightRadiusUnit("px");
        }

        if (
          typeof resolvedStyle.borderBottomLeftRadius === "string" &&
          resolvedStyle.borderBottomLeftRadius.endsWith("%")
        ) {
          setBorderBottomLeftRadiusUnit("%");
        } else {
          setBorderBottomLeftRadiusUnit("px");
        }

        if (
          typeof resolvedStyle.borderBottomRightRadius === "string" &&
          resolvedStyle.borderBottomRightRadius.endsWith("%")
        ) {
          setBorderBottomRightRadiusUnit("%");
        } else {
          setBorderBottomRightRadiusUnit("px");
        }

        // Only update if different to avoid unnecessary updates while user is typing
        if (newWidth !== localWidth && !widthInputRef.current?.matches(":focus")) {
          setLocalWidth(newWidth);
        }
        if (newHeight !== localHeight && !heightInputRef.current?.matches(":focus")) {
          setLocalHeight(newHeight);
        }

        // Update min size fields
        setLocalMinWidth(newMinWidth);
        setLocalMinHeight(newMinHeight);

        // Update max size fields
        setLocalMaxWidth(newMaxWidth);
        setLocalMaxHeight(newMaxHeight);

        // Update aspect ratio field
        const newAspectRatio = selectedComponent.size.aspectRatio || "";
        setLocalAspectRatio(newAspectRatio);

        // Update position fields
        setLocalLeft(newLeft);
        setLocalTop(newTop);
        setLocalRight(newRight);
        setLocalBottom(newBottom);

        // Update border radius field
        const newBorderRadius =
          resolvedStyle.borderRadius !== undefined &&
          resolvedStyle.borderRadius !== 0
            ? resolvedStyle.borderRadius.toString()
            : "";
        setLocalBorderRadius(newBorderRadius);

        // Update border state
        const resolvedBorderStyle =
          resolvedStyle.borderStyle === "none"
            ? "solid"
            : resolvedStyle.borderStyle || "solid";
        setLocalBorderStyle(resolvedBorderStyle);
        // Only set as default if borderStyle is undefined (not explicitly set)
        if (resolvedStyle.borderStyle === undefined) {
          setIsDefaultBorderStyle(true);
        } else {
          setIsDefaultBorderStyle(false);
        }
        setLocalBorderWidth(resolvedStyle.borderWidth?.toString() || "1");
        setLocalBorderColor(resolvedStyle.borderColor || "#000000");

        // Update individual border width states
        setLocalBorderTopWidth(resolvedStyle.borderTopWidth?.toString() || "");
        setLocalBorderRightWidth(resolvedStyle.borderRightWidth?.toString() || "");
        setLocalBorderBottomWidth(resolvedStyle.borderBottomWidth?.toString() || "");
        setLocalBorderLeftWidth(resolvedStyle.borderLeftWidth?.toString() || "");

        // Update individual border radius states
        setLocalBorderTopLeftRadius(resolvedStyle.borderTopLeftRadius?.toString() || "");
        setLocalBorderTopRightRadius(
          resolvedStyle.borderTopRightRadius?.toString() || "",
        );
        setLocalBorderBottomLeftRadius(
          resolvedStyle.borderBottomLeftRadius?.toString() || "",
        );
        setLocalBorderBottomRightRadius(
          resolvedStyle.borderBottomRightRadius?.toString() || "",
        );

        // Update padding states
        const newPadding =
          resolvedStyle.padding !== undefined && resolvedStyle.padding !== 0
            ? resolvedStyle.padding.toString()
            : "";
        setLocalPadding(newPadding);
        setLocalPaddingTop(resolvedStyle.paddingTop?.toString() || "");
        setLocalPaddingRight(resolvedStyle.paddingRight?.toString() || "");
        setLocalPaddingBottom(resolvedStyle.paddingBottom?.toString() || "");
        setLocalPaddingLeft(resolvedStyle.paddingLeft?.toString() || "");

        // Update margin state (uniform only)
        if (selectedComponent.margin) {
          const { top, right, bottom, left } = selectedComponent.margin;
          const isUniform = top === right && top === bottom && top === left;
          setLocalMargin(isUniform ? top.toString() : "");
          setLocalMarginTop(top.toString());
          setLocalMarginRight(right.toString());
          setLocalMarginBottom(bottom.toString());
          setLocalMarginLeft(left.toString());
        } else {
          setLocalMargin("");
          setLocalMarginTop("");
          setLocalMarginRight("");
          setLocalMarginBottom("");
          setLocalMarginLeft("");
        }

        // Update backdrop filter state
        const backdropFilter = resolvedStyle.backdropFilter;
        if (backdropFilter) {
          const match = backdropFilter.match(/^(\w+(?:-\w+)?)\(([^)]+)\)$/);
          if (match) {
            const [, filterType, valueWithUnit] = match;
            setLocalBackdropFilterType(filterType);

            // Extract numeric value and unit
            const numericValue = valueWithUnit.replace(/[^\d.]/g, "");
            const unit = valueWithUnit.replace(/[\d.]/g, "") as "px" | "%" | "deg";

            setLocalBackdropFilterValue(numericValue);
            setBackdropFilterUnit(unit || "px");

            // Store original values for reset functionality
            setOriginalBackdropFilterValue(numericValue);
            setOriginalBackdropFilterUnit(unit || "px");
          }
        } else {
          // Reset to defaults if no backdrop filter
          setLocalBackdropFilterType("blur");
          setLocalBackdropFilterValue("0");
          setBackdropFilterUnit("px");
          setOriginalBackdropFilterValue("0");
          setOriginalBackdropFilterUnit("px");
        }
      }, 100); // 100ms delay to ensure component has rendered

      return () => clearTimeout(timer);
    }
  }, [selectedComponent, localWidth, localHeight, selectedComponent?.id, skinStyleTab]);

  if (!selectedComponent) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-400">No component selected</p>
      </div>
    );
  }

  const handleUpdate = (updates: Partial<SkinComponent>) => {
    if (updates.style && styleKey !== "style") {
      const nextStyle = updates.style as Record<string, unknown>;
      onUpdateComponent(selectedComponent.id, {
        ...updates,
        style: undefined,
        [styleKey]: nextStyle,
      });
      return;
    }
    onUpdateComponent(selectedComponent.id, updates);
  };

  const applyUniformMargin = (value: string) => {
    if (value === "") {
      handleUpdate({
        margin: { top: 0, right: 0, bottom: 0, left: 0 },
      });
      setLocalMarginTop("");
      setLocalMarginRight("");
      setLocalMarginBottom("");
      setLocalMarginLeft("");
      return;
    }
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) return;
    handleUpdate({
      margin: {
        top: numericValue,
        right: numericValue,
        bottom: numericValue,
        left: numericValue,
      },
    });
    setLocalMarginTop(numericValue.toString());
    setLocalMarginRight(numericValue.toString());
    setLocalMarginBottom(numericValue.toString());
    setLocalMarginLeft(numericValue.toString());
  };

  const getTextDecorationParts = () => {
    const raw =
      typeof resolvedStyle.textDecoration === "string"
        ? resolvedStyle.textDecoration
        : "";
    return new Set(
      raw
        .split(" ")
        .map((part) => part.trim())
        .filter(Boolean),
    );
  };

  const toggleTextDecoration = (part: "underline" | "line-through") => {
    const parts = getTextDecorationParts();
    if (parts.has(part)) {
      parts.delete(part);
    } else {
      parts.add(part);
    }
    const nextDecoration = Array.from(parts).join(" ");
    handleUpdate({
      style: {
        ...resolvedStyle,
        textDecoration: nextDecoration || undefined,
      },
    });
  };

  const readActions = (currentContent: Record<string, unknown>): Array<Record<string, unknown>> => {
    const actions = (currentContent.actions as Array<Record<string, unknown>>) || [];
    if (actions.length > 0) return actions;

    const legacyActions: Array<Record<string, unknown>> = [];

    if (currentContent.linkedPanoramaId) {
      legacyActions.push({
        id: "legacy-navigate",
        type: "navigate",
        trigger: "click",
        linkedPanoramaId: currentContent.linkedPanoramaId,
        linkTransitionType: currentContent.linkTransitionType,
        linkTransitionViewMode: currentContent.linkTransitionViewMode,
        linkTransitionDirectionView: currentContent.linkTransitionDirectionView,
        linkedPanoramaView: currentContent.linkedPanoramaView,
        linkTransitionFadeColor: currentContent.linkTransitionFadeColor,
        linkTransitionFadeDurationSec: currentContent.linkTransitionFadeDurationSec,
      });
    }

    if (currentContent.notificationTitle || currentContent.notificationMessage) {
      legacyActions.push({
        id: "legacy-notification",
        type: "notification",
        trigger: "click",
        notificationTitle: currentContent.notificationTitle,
        notificationMessage: currentContent.notificationMessage,
        notificationAutoHideDuration: currentContent.notificationAutoHideDuration,
        notificationStyle: currentContent.notificationStyle,
      });
    }

    if (currentContent.downloadFileUrl) {
      legacyActions.push({
        id: "legacy-download",
        type: "download",
        trigger: "click",
        downloadFileUrl: currentContent.downloadFileUrl,
        downloadFileName: currentContent.downloadFileName,
      });
    }

    if (
      currentContent.skinVisibilityAction &&
      Array.isArray(currentContent.skinVisibilityTargetComponentIds)
    ) {
      legacyActions.push({
        id: "legacy-skin-visibility",
        type: "showHideSkin",
        trigger: "click",
        skinVisibilityAction: currentContent.skinVisibilityAction,
        skinVisibilityTargetComponentIds: currentContent.skinVisibilityTargetComponentIds,
      });
    }

    if (currentContent.hotspotVisibilityAction) {
      legacyActions.push({
        id: "legacy-hotspot-visibility",
        type: "showHideHotspots",
        trigger: "click",
        hotspotVisibilityAction: currentContent.hotspotVisibilityAction,
        hotspotVisibilityTargetIds: currentContent.hotspotVisibilityTargetIds,
        hotspotVisibilityItemsConfig: currentContent.hotspotVisibilityItemsConfig,
      });
    }

    return legacyActions;
  };

  const writeActions = (actions: Array<Record<string, unknown>>) => {
    const currentContent = (selectedComponent.content as Record<string, unknown>) || {};
    const nextContent: Record<string, unknown> = {
      ...currentContent,
      actions,
      linkedPanoramaId: undefined,
      linkTransitionType: undefined,
      linkTransitionViewMode: undefined,
      linkTransitionDirectionView: undefined,
      linkedPanoramaView: undefined,
      linkTransitionFadeColor: undefined,
      linkTransitionFadeDurationSec: undefined,
      notificationTitle: undefined,
      notificationMessage: undefined,
      notificationAutoHideDuration: undefined,
      notificationStyle: undefined,
      downloadFileUrl: undefined,
      downloadFileName: undefined,
      skinVisibilityAction: undefined,
      skinVisibilityTargetComponentIds: undefined,
      hotspotVisibilityAction: undefined,
      hotspotVisibilityTargetIds: undefined,
      hotspotVisibilityItemsConfig: undefined,
    };
    handleUpdate({ content: nextContent });
  };

  const addAction = (type: string) => {
    const currentContent = (selectedComponent.content as Record<string, unknown>) || {};
    const actions = readActions(currentContent);
    const newActionId = crypto.randomUUID();
    const newAction: Record<string, unknown> = { id: newActionId, type, trigger: "click" };

    if (type === "notification") {
      newAction.notificationStyle = "toast";
    } else if (type === "showHideSkin") {
      newAction.skinVisibilityAction = "show";
    } else if (type === "showHideHotspots") {
      newAction.hotspotVisibilityAction = "show";
      setHotspotVisibilityConfigs((prev) => ({
        ...prev,
        [newActionId]: { mode: "selected", filters: [], sort: null, selectedItemIds: [] },
      }));
    }

    writeActions([...actions, newAction]);
    setExpandedActionIds((prev) => new Set([...prev, newActionId]));
    setIsActionMenuOpen(false);
  };

  const removeAction = (actionId: string) => {
    const currentContent = (selectedComponent.content as Record<string, unknown>) || {};
    const actions = readActions(currentContent);
    writeActions(actions.filter((action) => (action.id as string) !== actionId));
    setExpandedActionIds((prev) => {
      const next = new Set(prev);
      next.delete(actionId);
      return next;
    });
  };

  const updateAction = (actionId: string, updates: Record<string, unknown>) => {
    const currentContent = (selectedComponent.content as Record<string, unknown>) || {};
    const actions = readActions(currentContent);
    writeActions(
      actions.map((action) =>
        (action.id as string) === actionId ? { ...action, ...updates } : action,
      ),
    );
  };

  const handleApplySkinStylePreset = (preset: SkinStylePreset) => {
    if (!selectedComponent) return;
    setSelectedSkinStylePresetId(preset.id);
    setSkinStylePresetInputValue("");
    handleUpdate({
      style: { ...resolvedStyle, ...preset.style },
      content: { ...content, skinStylePresetId: preset.id },
    });
  };

  const handleCreateSkinStylePreset = () => {
    if (!selectedComponent) return;
    const trimmedName = skinStylePresetInputValue.trim();
    if (!trimmedName) return;
    if (normalizeStyleName(trimmedName) === SKIN_LOCAL_STYLE_ID) {
      setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
      setSkinStylePresetInputValue("");
      return;
    }

    const existing = matchingSkinPresetByInput;
    if (existing) {
      handleApplySkinStylePreset(existing);
      return;
    }

    const preset: SkinStylePreset = {
      id: crypto.randomUUID(),
      name: trimmedName,
      componentType: selectedComponent.type,
      style: { ...resolvedStyle },
    };
    const nextPresets = [...skinStylePresets, preset];
    void persistSkinStylePresets(nextPresets);
    handleApplySkinStylePreset(preset);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-scroll [scrollbar-gutter:stable]">
        <div className="px-3 pt-0 pb-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="justify-center text-muted-foreground sticky top-0 z-20 grid h-10 min-h-10 w-full grid-cols-3 items-stretch bg-gray-800/95 p-0 pb-0 mb-1 rounded-none border-b border-gray-700 backdrop-blur">
              <TabsTrigger
                value="style"
                className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative flex h-full min-h-full items-center justify-center whitespace-nowrap rounded-none bg-transparent px-3 py-0 text-[11px] font-semibold leading-none tracking-[0.2em] text-gray-400 transition-colors hover:bg-gray-700/40 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-transparent after:content-[''] data-[state=active]:after:bg-blue-500"
              >
                STYLING
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative flex h-full min-h-full items-center justify-center whitespace-nowrap rounded-none bg-transparent px-3 py-0 text-[11px] font-semibold leading-none tracking-[0.2em] text-gray-400 transition-colors hover:bg-gray-700/40 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-transparent after:content-[''] data-[state=active]:after:bg-blue-500"
              >
                SETTINGS
              </TabsTrigger>
              <TabsTrigger
                value="actions"
                className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative flex h-full min-h-full items-center justify-center whitespace-nowrap rounded-none bg-transparent px-3 py-0 text-[11px] font-semibold leading-none tracking-[0.2em] text-gray-400 transition-colors hover:bg-gray-700/40 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-transparent after:content-[''] data-[state=active]:after:bg-blue-500"
              >
                ACTIONS
              </TabsTrigger>
            </TabsList>

            <TabsContent value="style" className="space-y-3 mt-4">
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Skin Styles</h3>
                <div className="space-y-2">
                  <div className="relative">
                    <div
                      className="flex h-8 items-center rounded-md border border-gray-700 bg-gray-700/60"
                      onMouseDown={(e) => {
                        if ((e.target as HTMLElement).tagName === "INPUT") {
                          return;
                        }
                        if ((e.target as HTMLElement).closest("[data-style-source-chip]")) {
                          return;
                        }
                        e.preventDefault();
                        setIsSkinStyleDropdownOpen(true);
                        requestAnimationFrame(() => skinStylePresetInputRef.current?.focus());
                      }}
                    >
                      <div className="flex flex-1 items-center gap-1 px-2">
                        {selectedSkinStylePreset &&
                          selectedSkinStylePresetId !== SKIN_LOCAL_STYLE_ID && (
                            <button
                              type="button"
                              data-style-source-chip="true"
                              className="inline-flex h-6 max-w-[120px] items-center gap-1 rounded bg-[#006ACC] px-2 text-[11px] font-medium leading-none text-white"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
                                setSkinStylePresetInputValue("");
                              }}
                            >
                              <span className="truncate">{selectedSkinStylePreset.name}</span>
                              <span className="text-[10px] text-white/80">✕</span>
                            </button>
                          )}
                        <button
                          type="button"
                          data-style-source-chip="true"
                          className={`inline-flex h-6 items-center gap-1 rounded px-2 text-[11px] font-medium leading-none transition-colors ${
                            selectedSkinStylePresetId === SKIN_LOCAL_STYLE_ID
                              ? "bg-[#006ACC] text-white"
                              : "bg-gray-600 text-gray-100 hover:bg-gray-500"
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedSkinStylePresetId(SKIN_LOCAL_STYLE_ID);
                            setSkinStylePresetInputValue("");
                          }}
                        >
                          <span className="truncate">local</span>
                        </button>
                        <input
                          ref={skinStylePresetInputRef}
                          value={skinStylePresetInputValue}
                          onChange={(e) => {
                            setSkinStylePresetInputValue(e.target.value);
                            setIsSkinStyleDropdownOpen(true);
                          }}
                          onFocus={() => setIsSkinStyleDropdownOpen(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCreateSkinStylePreset();
                              setIsSkinStyleDropdownOpen(false);
                            } else if (e.key === "Escape") {
                              setIsSkinStyleDropdownOpen(false);
                            }
                          }}
                          placeholder=""
                          className="min-w-[56px] flex-1 bg-transparent px-1 text-xs text-white outline-none placeholder:text-gray-500"
                        />
                      </div>
                      <div className="flex flex-shrink-0 items-center self-stretch border-l border-gray-600 px-2">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-200"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setIsSkinStyleDropdownOpen((open) => !open);
                            requestAnimationFrame(() =>
                              skinStylePresetInputRef.current?.focus(),
                            );
                          }}
                          aria-label="Toggle skin style list"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </div>

                    {isSkinStyleDropdownOpen &&
                      (compatibleSkinStylePresets.length > 0 ||
                        skinStylePresetInputValue.trim() ||
                        selectedSkinStylePresetId) && (
                        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-600 bg-gray-700 shadow-lg">
                          {skinStylePresetInputValue.trim() &&
                            !matchingSkinPresetByInput &&
                            normalizeStyleName(skinStylePresetInputValue) !==
                              SKIN_LOCAL_STYLE_ID && (
                              <>
                                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-300">
                                  New token
                                </div>
                                <div
                                  className="px-3 py-2 text-left text-xs text-white hover:bg-gray-600 cursor-pointer"
                                  onMouseDown={(e) => {
                                    if (e.button !== 0) return;
                                    e.preventDefault();
                                    handleCreateSkinStylePreset();
                                    setIsSkinStyleDropdownOpen(false);
                                  }}
                                >
                                  Create{" "}
                                  <span className="rounded bg-[#006ACC] px-1 text-white">
                                    {skinStylePresetInputValue.trim()}
                                  </span>
                                </div>
                              </>
                            )}
                          <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-300">
                            Global tokens
                          </div>
                          {filteredSkinStylePresets.length === 0 && (
                            <div className="px-3 py-2 text-xs text-gray-400">None yet</div>
                          )}
                          {filteredSkinStylePresets.map((preset) => (
                            <div
                              key={preset.id}
                              className={`flex items-center justify-between px-3 py-2 text-left text-xs group cursor-pointer ${
                                preset.id === selectedSkinStylePresetId
                                  ? "bg-blue-500/30 text-blue-100"
                                  : "text-white hover:bg-gray-600"
                              }`}
                              onMouseDown={(e) => {
                                if (e.button !== 0) return;
                                e.preventDefault();
                                handleApplySkinStylePreset(preset);
                                setIsSkinStyleDropdownOpen(false);
                              }}
                            >
                              <span className="flex-1">{preset.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Select
                        value={skinBreakpointKey}
                        onValueChange={(value) =>
                          setSkinBreakpointKey(value as "base" | SkinBreakpointKey)
                        }
                      >
                        <SelectTrigger className="h-8 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white focus:ring-0 focus:ring-offset-0">
                          <SelectValue placeholder="Base" />
                        </SelectTrigger>
                        <SelectContent side="bottom" align="start">
                          <SelectItem value="base">
                            <div className="flex items-center gap-2">
                              <span>Base</span>
                            </div>
                          </SelectItem>
                          {VIEWPORT_PRESETS.map((preset) => (
                            <SelectItem key={preset.key} value={preset.key}>
                              <div className="flex items-center gap-2">
                                <span>{preset.label}</span>
                                <span className="text-gray-400 text-[11px]">
                                  ≤ {preset.width}px
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Select
                        value={skinStyleTab}
                        onValueChange={(value) =>
                          setSkinStyleTab(value as "base" | "hover" | "active" | "visited")
                        }
                      >
                        <SelectTrigger className="h-8 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white focus:ring-0 focus:ring-offset-0">
                          <SelectValue placeholder="Normal" />
                        </SelectTrigger>
                        <SelectContent side="bottom" align="start">
                          <SelectItem value="base">
                            <div className="flex items-center gap-2">
                              <MousePointer2 className="h-3.5 w-3.5" />
                              <span>Normal</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="hover">
                            <div className="flex items-center gap-2">
                              <SquareMousePointer className="h-3.5 w-3.5" />
                              <span>Hover</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="active">
                            <div className="flex items-center gap-2">
                              <Check className="h-3.5 w-3.5" />
                              <span>Active</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="visited">
                            <div className="flex items-center gap-2">
                              <Eye className="h-3.5 w-3.5" />
                              <span>Visited</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />

              {/* Typography - show for all text components */}
              {selectedComponent.type === "text" && (
                <>
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">Typography</h3>

                    <div className="space-y-1">

                      {/* Text Color */}
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1">
                            <ColorPicker
                              value={resolvedStyle.color || "#ffffff"}
                              onChange={(color) =>
                                handleUpdate({
                                  style: { ...resolvedStyle, color: color },
                                })
                              }
                              label="Text Color"
                              labelClassName={
                                resolvedStyle.color &&
                                resolvedStyle.color !== "#ffffff"
                                  ? "text-xs p-0.5 rounded mb-1 cursor-pointer text-blue-400 bg-blue-400/20"
                                  : "text-xs p-0.5 rounded mb-1 cursor-pointer text-gray-400"
                              }
                              variant="compact"
                              onReset={() =>
                                handleUpdate({
                                  style: { ...resolvedStyle, color: "#ffffff" },
                                })
                              }
                              includeOpacity={false}
                            />
                          </div>
                          <VariableSelector
                            type="color"
                            onSelectVariable={(value) =>
                              handleUpdate({
                                style: { ...resolvedStyle, color: value },
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Font Size */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${resolvedStyle.fontSize && resolvedStyle.fontSize !== "16px" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              if (resolvedStyle.fontSize) {
                                handleUpdate({
                                  style: { ...resolvedStyle, fontSize: undefined },
                                });
                              }
                            }
                          }}
                        >
                          Font Size
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            value={
                              resolvedStyle.fontSize
                                ? resolvedStyle.fontSize.replace("px", "")
                                : "16"
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleUpdate({
                                  style: { ...resolvedStyle, fontSize: undefined },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue) && numValue >= 0) {
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      fontSize: `${numValue}px`,
                                    },
                                  });
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleUpdate({
                                  style: { ...resolvedStyle, fontSize: "16px" },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (Number.isNaN(numValue) || numValue < 0) {
                                  handleUpdate({
                                    style: { ...resolvedStyle, fontSize: "16px" },
                                  });
                                } else {
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      fontSize: `${numValue}px`,
                                    },
                                  });
                                }
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.currentTarget.blur();
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white w-full pr-12"
                            placeholder="16"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>

                      {/* Font Family */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${resolvedStyle.fontFamily && resolvedStyle.fontFamily !== "Arial, sans-serif" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              if (resolvedStyle.fontFamily) {
                                handleUpdate({
                                  style: { ...resolvedStyle, fontFamily: undefined },
                                });
                              }
                            }
                          }}
                        >
                          Font Family
                        </Label>
                        <Select
                          value={resolvedStyle.fontFamily || "Arial, sans-serif"}
                          onValueChange={(value) =>
                            handleUpdate({
                              style: { ...resolvedStyle, fontFamily: value },
                            })
                          }
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-6 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <button
                              type="button"
                              className="mx-2 my-2 flex w-[calc(100%-1rem)] items-center justify-between rounded bg-gray-700 px-2 py-1 text-[10px] text-white hover:bg-gray-600"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                fontUploadInputRef.current?.click();
                              }}
                            >
                              <span className="truncate">Upload font file</span>
                              <span className="text-gray-300">Browse</span>
                            </button>
                            {(craftspaceFontFamilies.length > 0 || projectFontFamilies.length > 0) && (
                              <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-gray-400">
                                Font Variables
                              </div>
                            )}
                            {craftspaceFontFamilies.map((variable) => (
                              <SelectItem
                                key={`ws-font-${variable.id}`}
                                value={toCSSVarReference(variable.name)}
                                className="text-xs"
                              >
                                <div className="flex w-full items-center justify-between">
                                  <span className="truncate">{variable.name}</span>
                                  <span
                                    className="ml-2 text-[10px] text-gray-400"
                                    style={{ fontFamily: variable.value }}
                                  >
                                    Aa
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                            {projectFontFamilies.map((variable) => (
                              <SelectItem
                                key={`proj-font-${variable.id}`}
                                value={toCSSVarReference(variable.name)}
                                className="text-xs"
                              >
                                <div className="flex w-full items-center justify-between">
                                  <span className="truncate">{variable.name}</span>
                                  <span
                                    className="ml-2 text-[10px] text-gray-400"
                                    style={{ fontFamily: variable.value }}
                                  >
                                    Aa
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                            {(craftspaceFontFamilies.length > 0 || projectFontFamilies.length > 0) && (
                              <div className="my-1 h-px bg-gray-700" />
                            )}
                            {resolvedStyle.fontFamily &&
                              (resolvedStyle.fontFamily.startsWith("var(") ||
                                resolvedStyle.fontFamily.startsWith("--")) && (
                                <SelectItem
                                  value={resolvedStyle.fontFamily}
                                  className="text-xs"
                                >
                                  Variable ({resolvedStyle.fontFamily})
                                </SelectItem>
                              )}
                            <SelectItem value="Arial, sans-serif" className="text-xs">
                              Arial
                            </SelectItem>
                            <SelectItem value="Helvetica, sans-serif">Helvetica</SelectItem>
                            <SelectItem value="Georgia, serif">Georgia</SelectItem>
                            <SelectItem value="Times New Roman, serif" className="text-xs">
                              Times New Roman
                            </SelectItem>
                            <SelectItem value="Courier New, monospace" className="text-xs">
                              Courier New
                            </SelectItem>
                            <SelectItem value="Verdana, sans-serif">Verdana</SelectItem>
                            <SelectItem value="Impact, sans-serif">Impact</SelectItem>
                            <SelectItem value="Comic Sans MS, cursive">Comic Sans MS</SelectItem>
                          </SelectContent>
                        </Select>
                        <input
                          ref={fontUploadInputRef}
                          type="file"
                          accept=".woff2,.woff,.ttf,.otf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            handleFontUpload(file);
                            e.currentTarget.value = "";
                          }}
                        />
                      </div>

                      {/* Font Weight */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${resolvedStyle.fontWeight && resolvedStyle.fontWeight !== "normal" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              if (resolvedStyle.fontWeight) {
                                handleUpdate({
                                  style: { ...resolvedStyle, fontWeight: undefined },
                                });
                              }
                            }
                          }}
                        >
                          Font Weight
                        </Label>
                        <Select
                          value={resolvedStyle.fontWeight || "normal"}
                          onValueChange={(value) =>
                            handleUpdate({
                              style: { ...resolvedStyle, fontWeight: value },
                            })
                          }
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-6 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal" className="text-xs">
                              Normal
                            </SelectItem>
                            <SelectItem value="bold" className="text-xs">
                              Bold
                            </SelectItem>
                            <SelectItem value="lighter">Lighter</SelectItem>
                            <SelectItem value="bolder" className="text-xs">
                              Bolder
                            </SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="200">200</SelectItem>
                            <SelectItem value="300">300</SelectItem>
                            <SelectItem value="400">400</SelectItem>
                            <SelectItem value="500">500</SelectItem>
                            <SelectItem value="600">600</SelectItem>
                            <SelectItem value="700">700</SelectItem>
                            <SelectItem value="800">800</SelectItem>
                            <SelectItem value="900">900</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Text Formatting */}
                      <div className="space-y-1">
                        {(() => {
                          const textAlign =
                            typeof resolvedStyle.textAlign === "string"
                              ? resolvedStyle.textAlign
                              : "left";
                          const textTransform =
                            typeof resolvedStyle.textTransform === "string"
                              ? resolvedStyle.textTransform
                              : "none";
                          const fontStyle =
                            typeof resolvedStyle.fontStyle === "string"
                              ? resolvedStyle.fontStyle
                              : "normal";
                          const textDecorationParts = getTextDecorationParts();
                          const isUnderline = textDecorationParts.has("underline");
                          const isStrike = textDecorationParts.has("line-through");
                          const hasFormattingOverrides =
                            textAlign !== "left" ||
                            textTransform !== "none" ||
                            fontStyle === "italic" ||
                            isUnderline ||
                            isStrike;

                          return (
                            <>
                              <Label
                                className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${
                                  hasFormattingOverrides
                                    ? "text-blue-400 bg-blue-400/20"
                                    : "text-gray-400"
                                }`}
                                onClick={(e) => {
                                  if (e.altKey || e.metaKey || e.ctrlKey) {
                                    e.preventDefault();
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        textAlign: undefined,
                                        textTransform: undefined,
                                        textDecoration: undefined,
                                        fontStyle: undefined,
                                      },
                                    });
                                  }
                                }}
                              >
                                Text Formatting
                              </Label>
                              <div className="space-y-1">
                                <div className="flex gap-1">
                                  <div className="basis-1/2 min-w-0 grid grid-cols-4 gap-1">
                                    {[
                                      { key: "left", icon: AlignLeft, label: "Align Left" },
                                      { key: "center", icon: AlignCenter, label: "Align Center" },
                                      { key: "right", icon: AlignRight, label: "Align Right" },
                                      { key: "justify", icon: AlignJustify, label: "Justify" },
                                    ].map(({ key, icon: Icon, label }) => {
                                      const isSelected = textAlign === key;
                                      return (
                                        <Button
                                          key={key}
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className={`h-7 w-full p-0 ${
                                            isSelected
                                              ? "bg-slate-500/30 text-slate-100"
                                              : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                                          }`}
                                          onClick={() =>
                                            handleUpdate({
                                              style: { ...resolvedStyle, textAlign: key },
                                            })
                                          }
                                          title={label}
                                        >
                                          <Icon size={14} />
                                        </Button>
                                      );
                                    })}
                                  </div>
                                  <div className="basis-1/2 min-w-0 grid grid-cols-2 gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-full p-0 ${
                                        isUnderline
                                          ? "bg-slate-500/30 text-slate-100"
                                          : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                                      }`}
                                      onClick={() => toggleTextDecoration("underline")}
                                      title="Underline"
                                    >
                                      <Underline size={14} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-full p-0 ${
                                        isStrike
                                          ? "bg-slate-500/30 text-slate-100"
                                          : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                                      }`}
                                      onClick={() => toggleTextDecoration("line-through")}
                                      title="Strikethrough"
                                    >
                                      <Strikethrough size={14} />
                                    </Button>
                                  </div>
                                </div>

                                <div className="flex gap-1">
                                  <div className="basis-1/2 min-w-0 grid grid-cols-4 gap-1">
                                    {[
                                      { key: "none", label: "AB", title: "No Case Transform" },
                                      { key: "capitalize", label: "Aa", title: "Capitalize" },
                                      { key: "uppercase", label: "AB", title: "Uppercase" },
                                      { key: "lowercase", label: "ab", title: "Lowercase" },
                                    ].map(({ key, label, title }) => {
                                      const isSelected = textTransform === key;
                                      return (
                                        <Button
                                          key={key}
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className={`h-7 w-full p-0 text-[11px] font-semibold ${
                                            isSelected
                                              ? "bg-slate-500/30 text-slate-100"
                                              : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                                          }`}
                                          onClick={() =>
                                            handleUpdate({
                                              style: {
                                                ...resolvedStyle,
                                                textTransform: key === "none" ? undefined : key,
                                              },
                                            })
                                          }
                                          title={title}
                                        >
                                          {label}
                                        </Button>
                                      );
                                    })}
                                  </div>
                                  <div className="basis-1/2 min-w-0 grid grid-cols-1 gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-full p-0 ${
                                        fontStyle === "italic"
                                          ? "bg-slate-500/30 text-slate-100"
                                          : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                                      }`}
                                      onClick={() =>
                                        handleUpdate({
                                          style: {
                                            ...resolvedStyle,
                                            fontStyle: fontStyle === "italic" ? undefined : "italic",
                                          },
                                        })
                                      }
                                      title="Italic"
                                    >
                                      <Italic size={14} />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Line Height */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${resolvedStyle.lineHeight && resolvedStyle.lineHeight !== "1.4" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              if (resolvedStyle.lineHeight) {
                                handleUpdate({
                                  style: { ...resolvedStyle, lineHeight: undefined },
                                });
                              }
                            }
                          }}
                        >
                          Line Height
                        </Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            variant="compact-wide"
                            value={resolvedStyle.lineHeight || "1.4"}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleUpdate({
                                  style: { ...resolvedStyle, lineHeight: undefined },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue) && numValue > 0) {
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      lineHeight: numValue.toString(),
                                    },
                                  });
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleUpdate({
                                  style: { ...resolvedStyle, lineHeight: "1.4" },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (Number.isNaN(numValue) || numValue <= 0) {
                                  handleUpdate({
                                    style: { ...resolvedStyle, lineHeight: "1.4" },
                                  });
                                } else {
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      lineHeight: numValue.toString(),
                                    },
                                  });
                                }
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.currentTarget.blur();
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="1.4"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                </>
              )}

              {selectedComponent.type === "image" && (
                <>
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">Image Properties</h3>

                    <div className="space-y-1">
                      {/* Image Source */}
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">
                          Image Source
                        </Label>
                        <div className="relative">
                          {(() => {
                            const parentComponent = skinComponents.find(
                              (c) => c.id === selectedComponent.parentId,
                            );
                            if (parentComponent?.type === "collection-list") {
                              // Show dropdown for collection list children
                              return (
                                <Select
                                  value={selectedComponent.content?.dataBinding || "none"}
                                  onValueChange={(value) =>
                                    handleUpdate({
                                      content: {
                                        ...selectedComponent.content,
                                        dataBinding: value === "none" ? undefined : value,
                                        src:
                                          value === "none"
                                            ? ""
                                            : selectedComponent.content?.src || "",
                                      },
                                    })
                                  }
                                >
                                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-6 pr-8">
                                    <SelectValue placeholder="Select image field..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-gray-700 border-gray-600">
                                    <SelectItem value="none" className="text-white text-xs">
                                      No image
                                    </SelectItem>
                                    {collectionFields
                                      .filter(
                                        (field) =>
                                          field.type === "text" ||
                                          field.type === "url" ||
                                          field.name.toLowerCase().includes("image") ||
                                          field.name.toLowerCase().includes("url"),
                                      )
                                      .map((field) => (
                                        <SelectItem
                                          key={field.name}
                                          value={field.name}
                                          className="text-white text-xs"
                                        >
                                          {field.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              );
                            } else {
                              // Show regular input for non-collection components
                              return (
                                <Input
                                  value={selectedComponent.content?.src || ""}
                                  onChange={(e) =>
                                    handleUpdate({
                                      content: {
                                        ...selectedComponent.content,
                                        src: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Enter image URL or upload file..."
                                  className="bg-gray-700 border-gray-600 text-white h-6 pr-8"
                                />
                              );
                            }
                          })()}
                          <DataBindingButton
                            componentId={selectedComponent.id}
                            componentType={selectedComponent.type}
                            propertyPath="content.src"
                            currentBinding={imageSourceBinding}
                            onBindingChange={handleImageSourceBindingChange}
                          />
                        </div>
                      </div>

                      {/* File Upload */}
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">
                          Upload Image
                        </Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setSelectedFileName(file.name);
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const result = event.target?.result as string;
                                  handleUpdate({
                                    content: { ...selectedComponent.content, src: result },
                                  });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            id="image-upload"
                          />
                          <label
                            htmlFor="image-upload"
                            className="flex items-center justify-between w-full h-6 px-3 py-1 text-xs bg-gray-600 text-white rounded cursor-pointer hover:bg-gray-500 transition-colors"
                          >
                            <span className="truncate">{selectedFileName || "Choose File"}</span>
                            <span className="ml-2 text-gray-300">Browse</span>
                          </label>
                        </div>
                      </div>

                      {/* Alt Text */}
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">Alt Text</Label>
                        <Input
                          value={selectedComponent.content?.alt || ""}
                          onChange={(e) =>
                            handleUpdate({
                              content: { ...selectedComponent.content, alt: e.target.value },
                            })
                          }
                          placeholder="Describe the image..."
                          className="bg-gray-700 border-gray-600 text-white h-6"
                        />
                      </div>

                      {/* Object Fit */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.content?.objectFit && selectedComponent.content?.objectFit !== "cover" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              handleUpdate({
                                content: { ...selectedComponent.content, objectFit: "cover" },
                              });
                            }
                          }}
                        >
                          Object Fit
                        </Label>
                        <Select
                          value={selectedComponent.content?.objectFit || "cover"}
                          onValueChange={(value) =>
                            handleUpdate({
                              content: { ...selectedComponent.content, objectFit: value },
                            })
                          }
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-6 text-xs">
                            <SelectValue placeholder="Select object fit" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            <SelectItem value="cover" className="text-xs">
                              Cover
                            </SelectItem>
                            <SelectItem value="contain" className="text-xs">
                              Contain
                            </SelectItem>
                            <SelectItem value="fill" className="text-xs">
                              Fill
                            </SelectItem>
                            <SelectItem value="scale-down" className="text-xs">
                              Scale Down
                            </SelectItem>
                            <SelectItem value="none" className="text-xs">
                              None
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                </>
              )}

              {selectedComponent.type === "icon" && (
                <>
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">Icon Properties</h3>

                    <div className="space-y-1">
                      {/* Lucide Icon Search */}
                      <LucideIconSearch
                        onIconSelect={handleLucideIconSelect}
                        selectedIcon={selectedComponent.content?.lucideIcon}
                        onClear={() =>
                          handleUpdate({
                            content: {
                              ...(selectedComponent.content || {}),
                              lucideIcon: undefined,
                            },
                          })
                        }
                      />

                      {/* File Upload */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.content?.src ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={() => {
                            if (selectedComponent.content?.src) {
                              handleUpdate({
                                content: { ...selectedComponent.content, src: "" },
                              });
                              setSelectedFileName("");
                            }
                          }}
                        >
                          Icon Source
                        </Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*,.svg"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setSelectedFileName(file.name);
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const result = event.target?.result as string;
                                  handleUpdate({
                                    content: { ...selectedComponent.content, src: result },
                                  });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            id="icon-upload"
                          />
                          <label
                            htmlFor="icon-upload"
                            className="flex items-center justify-between w-full h-6 px-3 py-1 text-xs bg-gray-600 text-white rounded cursor-pointer hover:bg-gray-500 transition-colors"
                          >
                            <span className="truncate">{selectedFileName || "Choose File"}</span>
                            <span className="ml-2 text-gray-300">Browse</span>
                          </label>
                        </div>
                      </div>

                      {/* SVG Code */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.content?.svg ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={() => {
                            if (selectedComponent.content?.svg) {
                              handleUpdate({
                                content: { ...selectedComponent.content, svg: "" },
                              });
                            }
                          }}
                        >
                          SVG Code
                        </Label>
                        <Textarea
                          value={localSvgMarkup}
                          onChange={(e) => setLocalSvgMarkup(e.target.value)}
                          onBlur={() => {
                            const sanitizedSvg = sanitizeSvgMarkup(localSvgMarkup);
                            handleUpdate({
                              content: { ...selectedComponent.content, svg: sanitizedSvg },
                            });
                            setLocalSvgMarkup(sanitizedSvg);
                          }}
                          placeholder="Paste SVG code here..."
                          className="bg-gray-700 border-gray-600 text-white text-xs min-h-[80px]"
                          rows={4}
                        />
                      </div>

                      {/* Icon Color */}
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1">
                            <ColorPicker
                              value={selectedComponent.content?.color || "#ffffff"}
                              onChange={(color) =>
                                handleUpdate({
                                  content: {
                                    ...(selectedComponent.content || {}),
                                    color: color || "#ffffff",
                                  },
                                })
                              }
                              label="Icon Color"
                              labelClassName={
                                selectedComponent.content?.color &&
                                selectedComponent.content?.color !== "#ffffff"
                                  ? "text-xs p-0.5 rounded mb-1 cursor-pointer text-blue-400 bg-blue-400/20"
                                  : "text-xs p-0.5 rounded mb-1 cursor-pointer text-gray-400"
                              }
                              variant="compact"
                              onReset={() =>
                                handleUpdate({
                                  content: {
                                    ...(selectedComponent.content || {}),
                                    color: "#ffffff",
                                  },
                                })
                              }
                              includeOpacity={false}
                            />
                          </div>
                          <VariableSelector
                            type="color"
                            onSelectVariable={(value) =>
                              handleUpdate({
                                content: { ...(selectedComponent.content || {}), color: value },
                              })
                            }
                          />
                        </div>
                      </div>

                      {/* Alt Text */}
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">Alt Text</Label>
                        <Input
                          value={selectedComponent.content?.alt || ""}
                          onChange={(e) =>
                            handleUpdate({
                              content: { ...selectedComponent.content, alt: e.target.value },
                            })
                          }
                          placeholder="Describe the icon..."
                          className="bg-gray-700 border-gray-600 text-white h-6"
                        />
                      </div>

                      {/* Object Fit */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.content?.objectFit && selectedComponent.content?.objectFit !== "contain" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              handleUpdate({
                                content: { ...selectedComponent.content, objectFit: "contain" },
                              });
                            }
                          }}
                        >
                          Object Fit
                        </Label>
                        <Select
                          value={selectedComponent.content?.objectFit || "contain"}
                          onValueChange={(value) =>
                            handleUpdate({
                              content: { ...selectedComponent.content, objectFit: value },
                            })
                          }
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-6 text-xs">
                            <SelectValue placeholder="Select object fit" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            <SelectItem value="contain" className="text-xs">
                              Contain
                            </SelectItem>
                            <SelectItem value="cover" className="text-xs">
                              Cover
                            </SelectItem>
                            <SelectItem value="fill" className="text-xs">
                              Fill
                            </SelectItem>
                            <SelectItem value="scale-down" className="text-xs">
                              Scale Down
                            </SelectItem>
                            <SelectItem value="none" className="text-xs">
                              None
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                </>
              )}

              {selectedComponent.type === "dropdown" && (
                <>
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">
                      Dropdown Properties
                    </h3>

                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">
                          CMS Collection
                        </Label>
                        <Select
                          value={
                            selectedComponent.content?.dataSourceId
                              ? (selectedComponent.content?.dataSourceId as string)
                              : "__none__"
                          }
                          onValueChange={(value) => {
                            if (value === "__none__") {
                              handleUpdate({
                                content: {
                                  ...(selectedComponent.content || {}),
                                  dataSourceId: "",
                                },
                              });
                              return;
                            }

                            const dataset = availableCollections.find((c) => c.id === value);
                            const fieldStructure = (dataset?.fieldStructure as Array<Record<string, unknown>>) || [];
                            const nameField =
                              fieldStructure.find((field) => field.name === "name")?.name ||
                              fieldStructure[0]?.name ||
                              "name";
                            const datasetName = (dataset?.name || "").toLowerCase();
                            const actionType = datasetName.includes("hotspot")
                              ? "hotspot"
                              : datasetName.includes("panorama")
                                ? "panorama"
                                : "none";

                            handleUpdate({
                              content: {
                                ...(selectedComponent.content || {}),
                                  dataSourceId: value,
                                  labelField: nameField,
                                  actionType,
                                  selectedIndex: 0,
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-7 text-xs">
                            <SelectValue placeholder="Select a collection" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            <SelectItem value="__none__" className="text-white text-xs">
                              Select a collection
                            </SelectItem>
                            {availableCollections.map((collection) => (
                              <SelectItem key={collection.id} value={collection.id} className="text-xs">
                                {collection.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedComponent.content?.dataSourceId ? (
                        <div className="space-y-1">
                          <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">
                            Label Field
                          </Label>
                          <Select
                            value={selectedComponent.content?.labelField || "name"}
                            onValueChange={(value) =>
                              handleUpdate({
                                content: {
                                  ...(selectedComponent.content || {}),
                                  labelField: value,
                                },
                              })
                            }
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-7 text-xs">
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-700 border-gray-600">
                              {dropdownFields.map((field) => (
                                <SelectItem
                                  key={String(field.name)}
                                  value={String(field.name)}
                                  className="text-xs"
                                >
                                  {String(field.displayName || field.name)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}

                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-400">
                          Placeholder
                        </Label>
                        <Input
                          value={selectedComponent.content?.placeholder || ""}
                          onChange={(e) =>
                            handleUpdate({
                              content: {
                                ...(selectedComponent.content || {}),
                                placeholder: e.target.value,
                              },
                            })
                          }
                          placeholder="Select option"
                          className="bg-gray-700 border-gray-600 text-white h-6"
                        />
                      </div>
                    </div>
                  </div>
                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                </>
              )}

              {/* Collection Properties - Only show for collection-list components */}
              {selectedComponent.type === "collection-list" && (
                <>
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">
                      Collection Properties
                    </h3>

                    <div className="space-y-1">
                      {/* Data Source */}
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.content?.dataSourceId ? "text-purple-400 bg-purple-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey && selectedComponent.content?.dataSourceId) {
                              // Alt+click to reset - also clear all child component bindings
                              const childComponents = skinComponents.filter(
                                (c) => c.parentId === selectedComponent.id,
                              );

                              // Update the collection list to remove data source
                              onUpdateComponent(selectedComponent.id, {
                                content: {
                                  ...selectedComponent.content,
                                  dataSourceId: undefined,
                                },
                              });

                              // Clear data bindings from all child components
                              childComponents.forEach((child) => {
                                if (child.content?.dataBinding) {
                                  onUpdateComponent(child.id, {
                                    content: {
                                      ...child.content,
                                      dataBinding: undefined,
                                    },
                                  });
                                }
                              });
                            }
                          }}
                        >
                          Data Source
                        </Label>
                        <Select
                          value={selectedComponent.content?.dataSourceId || "none"}
                          onValueChange={handleCollectionChange}
                          disabled={isLoadingCollections}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-8">
                            <SelectValue
                              placeholder={
                                isLoadingCollections
                                  ? "Loading collections..."
                                  : "Select a collection..."
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            <SelectItem value="none" className="text-white">
                              <span className="text-gray-400">No collection selected</span>
                            </SelectItem>
                            {availableCollections
                              .filter((collection) => collection?.id && collection.id.trim() !== "")
                              .map((collection) => (
                                <SelectItem
                                  key={collection.id}
                                  value={collection.id}
                                  className="text-white group"
                                >
                                  <div className="flex items-center gap-2">
                                    <Database className="h-3 w-3" />
                                    <span>{collection.name}</span>
                                    <span className="text-xs text-gray-400">
                                      ({collection.rowCount} items)
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Collection Items: Dynamic / Selected */}
                  {selectedComponent.content?.dataSourceId && (
                    <div className="mt-2">
                      <CollectionItemsPanel
                        config={
                          (selectedComponent.content?.collectionItemsConfig as CollectionItemsConfig) || {
                            mode: "dynamic",
                            filters: [],
                            sort: null,
                            selectedItemIds: [],
                          }
                        }
                        fields={collectionFields as Array<{ name: string; displayName?: string; fieldType?: string; isRequired?: boolean }>}
                        allItems={collectionItems}
                        onChange={(newConfig) => {
                          onUpdateComponent(selectedComponent.id, {
                            content: {
                              ...selectedComponent.content,
                              collectionItemsConfig: newConfig,
                            },
                          });
                        }}
                      />
                    </div>
                  )}

                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                </>
              )}

              {/* Conditional Visibility */}
              {showConditionalVisibility && (
                <div className="mt-1">
                  <h3 className="text-xs font-bold text-gray-300 pl-0.5">Conditional Visibility</h3>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between h-6 group relative">
                      <Label className="text-white text-xs p-0.5">Visible</Label>
                      <div className="relative">
                        <Switch
                          checked={selectedComponent.visible}
                          onCheckedChange={(checked) =>
                            handleUpdate({
                              visible: checked,
                            })
                          }
                          className="h-4 w-8 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                        />
                        <DataBindingButton
                          componentId={selectedComponent.id}
                          componentType={selectedComponent.type}
                          propertyPath="visible"
                          currentBinding={visibilityBinding}
                          onBindingChange={handleVisibilityBindingChange}
                          isPartOfCollectionList={isPartOfCollectionList}
                        />
                      </div>
                      <div
                        className="absolute left-0 top-full mt-2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 border border-gray-600"
                        style={{ maxWidth: "300px" }}
                      >
                        When disabled, the component is hidden from view.
                      </div>
                    </div>
                  </div>

                  {/* Conditions */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-white text-xs p-0.5">Conditions</Label>
                      <Button
                        type="button"
                        onClick={() => setShowConditionalVisibilityDialog(true)}
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-600"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>

                    {hasConditionalVisibility ? (
                      <div className="bg-gray-700 p-2 rounded-md">
                        <div className="text-xs text-gray-300 mb-1">
                          {conditionalVisibilityRules.length} condition
                          {conditionalVisibilityRules.length !== 1 ? "s" : ""} set
                        </div>
                        <div className="space-y-1">
                          {conditionalVisibilityRules.map((rule, _index) => (
                            <div key={rule.id} className="text-xs text-gray-400">
                              {rule.fieldDisplayName} {rule.operator.replace("_", " ")}{" "}
                              {rule.value || ""}
                            </div>
                          ))}
                        </div>
                        <Button
                          type="button"
                          onClick={handleRemoveConditionalVisibility}
                          variant="ghost"
                          size="sm"
                          className="mt-2 h-6 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Remove Conditions
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-2 text-gray-400 text-xs">None</div>
                    )}
                  </div>
                </div>
              )}

              {/* Size */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Size</h3>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.width !== "auto" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, width: "auto" },
                          });
                          setLocalWidth("");
                        }
                      }}
                    >
                      Width
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        ref={widthInputRef}
                        type="text"
                        value={localWidth === "auto" ? "auto" : localWidth}
                        onChange={(e) => setLocalWidth(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "" || value === "auto") {
                              handleUpdate({
                                size: { ...selectedComponent.size, width: "auto" },
                              });
                              setLocalWidth("");
                            } else if (value.endsWith("%")) {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                handleUpdate({
                                  size: { ...selectedComponent.size, width: `${numValue}%` },
                                });
                                setLocalWidth(numValue.toString());
                                setWidthUnit("%");
                              }
                            } else if (value.endsWith("px")) {
                              const numValue = parseInt(value, 10);
                              if (!Number.isNaN(numValue)) {
                                handleUpdate({
                                  size: { ...selectedComponent.size, width: numValue },
                                });
                                setLocalWidth(numValue.toString());
                                setWidthUnit("px");
                              }
                            } else {
                              const numValue = parseInt(value, 10);
                              if (!Number.isNaN(numValue)) {
                                // Check if original value was 'auto' or use the current unit state
                                const originalWidth = selectedComponent.size.width;
                                const shouldUsePercentage =
                                  originalWidth === "auto" || widthUnit === "%";

                                if (shouldUsePercentage) {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, width: `${numValue}%` },
                                  });
                                  setWidthUnit("%");
                                } else {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, width: numValue },
                                  });
                                  setWidthUnit("px");
                                }
                                setLocalWidth(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localWidth;

                          if (value === "" || value === "auto") {
                            handleUpdate({
                              size: { ...selectedComponent.size, width: "auto" },
                            });
                          } else if (value.endsWith("%")) {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              handleUpdate({
                                size: { ...selectedComponent.size, width: `${numValue}%` },
                              });
                              setLocalWidth(numValue.toString());
                              setWidthUnit("%");
                            }
                          } else if (value.endsWith("px")) {
                            const numValue = parseInt(value, 10);
                            if (!Number.isNaN(numValue)) {
                              handleUpdate({
                                size: { ...selectedComponent.size, width: numValue },
                              });
                              setLocalWidth(numValue.toString());
                              setWidthUnit("px");
                            }
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!Number.isNaN(numValue)) {
                              // Check if original value was 'auto' or use the current unit state
                              const originalWidth = selectedComponent.size.width;
                              const shouldUsePercentage =
                                originalWidth === "auto" || widthUnit === "%";

                              if (shouldUsePercentage) {
                                handleUpdate({
                                  size: { ...selectedComponent.size, width: `${numValue}%` },
                                });
                                setWidthUnit("%");
                              } else {
                                handleUpdate({
                                  size: { ...selectedComponent.size, width: numValue },
                                });
                                setWidthUnit("px");
                              }
                              setLocalWidth(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.size.width === "auto") return "auto";
                            if (
                              typeof selectedComponent.size.width === "string" &&
                              selectedComponent.size.width.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localWidth;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, width: `${numValue}%` },
                                  });
                                  setWidthUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, width: numValue },
                                  });
                                  setWidthUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, width: "10%" },
                                });
                                setLocalWidth("10");
                                setWidthUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, width: 10 },
                                });
                                setLocalWidth("10");
                                setWidthUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.size.width === "auto") return "-";
                                  if (
                                    typeof selectedComponent.size.width === "string" &&
                                    selectedComponent.size.width.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.height !== "auto" ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, height: "auto" },
                          });
                          setLocalHeight("");
                        }
                      }}
                    >
                      Height
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        ref={heightInputRef}
                        type="text"
                        value={localHeight === "auto" ? "auto" : localHeight}
                        onChange={(e) => setLocalHeight(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "" || value === "auto") {
                              handleUpdate({
                                size: { ...selectedComponent.size, height: "auto" },
                              });
                              setLocalHeight("");
                            } else if (value.endsWith("%")) {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                handleUpdate({
                                  size: { ...selectedComponent.size, height: `${numValue}%` },
                                });
                                setLocalHeight(numValue.toString());
                                setHeightUnit("%");
                              }
                            } else if (value.endsWith("px")) {
                              const numValue = parseInt(value, 10);
                              if (!Number.isNaN(numValue)) {
                                handleUpdate({
                                  size: { ...selectedComponent.size, height: numValue },
                                });
                                setLocalHeight(numValue.toString());
                                setHeightUnit("px");
                              }
                            } else {
                              const numValue = parseInt(value, 10);
                              if (!Number.isNaN(numValue)) {
                                // Check if original value was 'auto' or use the current unit state
                                const originalHeight = selectedComponent.size.height;
                                const shouldUsePercentage =
                                  originalHeight === "auto" || heightUnit === "%";

                                if (shouldUsePercentage) {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, height: `${numValue}%` },
                                  });
                                  setHeightUnit("%");
                                } else {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, height: numValue },
                                  });
                                  setHeightUnit("px");
                                }
                                setLocalHeight(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localHeight;

                          if (value === "" || value === "auto") {
                            handleUpdate({
                              size: { ...selectedComponent.size, height: "auto" },
                            });
                          } else if (value.endsWith("%")) {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              handleUpdate({
                                size: { ...selectedComponent.size, height: `${numValue}%` },
                              });
                              setLocalHeight(numValue.toString());
                              setHeightUnit("%");
                            }
                          } else if (value.endsWith("px")) {
                            const numValue = parseInt(value, 10);
                            if (!Number.isNaN(numValue)) {
                              handleUpdate({
                                size: { ...selectedComponent.size, height: numValue },
                              });
                              setLocalHeight(numValue.toString());
                              setHeightUnit("px");
                            }
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!Number.isNaN(numValue)) {
                              // Check if original value was 'auto' or use the current unit state
                              const originalHeight = selectedComponent.size.height;
                              const shouldUsePercentage =
                                originalHeight === "auto" || heightUnit === "%";

                              if (shouldUsePercentage) {
                                handleUpdate({
                                  size: { ...selectedComponent.size, height: `${numValue}%` },
                                });
                                setHeightUnit("%");
                              } else {
                                handleUpdate({
                                  size: { ...selectedComponent.size, height: numValue },
                                });
                                setHeightUnit("px");
                              }
                              setLocalHeight(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.size.height === "auto") return "auto";
                            if (
                              typeof selectedComponent.size.height === "string" &&
                              selectedComponent.size.height.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localHeight;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, height: `${numValue}%` },
                                  });
                                  setHeightUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, height: numValue },
                                  });
                                  setHeightUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, height: "10%" },
                                });
                                setLocalHeight("10");
                                setHeightUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, height: 10 },
                                });
                                setLocalHeight("10");
                                setHeightUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.size.height === "auto") return "-";
                                  if (
                                    typeof selectedComponent.size.height === "string" &&
                                    selectedComponent.size.height.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 order-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.minWidth !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, minWidth: undefined },
                          });
                          setLocalMinWidth("");
                        }
                      }}
                    >
                      Min Width
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localMinWidth}
                        onChange={(e) => setLocalMinWidth(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                size: { ...selectedComponent.size, minWidth: undefined },
                              });
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (minWidthUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minWidth: `${numValue}%` },
                                  });
                                } else {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minWidth: numValue },
                                  });
                                }
                                setLocalMinWidth(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localMinWidth;

                          if (value === "") {
                            handleUpdate({
                              size: { ...selectedComponent.size, minWidth: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (minWidthUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minWidth: `${numValue}%` },
                                });
                              } else {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minWidth: numValue },
                                });
                              }
                              setLocalMinWidth(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.size.minWidth === undefined) return "auto";
                            if (
                              typeof selectedComponent.size.minWidth === "string" &&
                              selectedComponent.size.minWidth.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localMinWidth;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minWidth: `${numValue}%` },
                                  });
                                  setMinWidthUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minWidth: numValue },
                                  });
                                  setMinWidthUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minWidth: "10%" },
                                });
                                setLocalMinWidth("10");
                                setMinWidthUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minWidth: 10 },
                                });
                                setLocalMinWidth("10");
                                setMinWidthUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.size.minWidth === undefined) return "-";
                                  if (
                                    typeof selectedComponent.size.minWidth === "string" &&
                                    selectedComponent.size.minWidth.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 order-3">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.minHeight !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, minHeight: undefined },
                          });
                          setLocalMinHeight("");
                        }
                      }}
                    >
                      Min Height
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localMinHeight}
                        onChange={(e) => setLocalMinHeight(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                size: { ...selectedComponent.size, minHeight: undefined },
                              });
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (minHeightUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minHeight: `${numValue}%` },
                                  });
                                } else {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minHeight: numValue },
                                  });
                                }
                                setLocalMinHeight(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localMinHeight;

                          if (value === "") {
                            handleUpdate({
                              size: { ...selectedComponent.size, minHeight: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (minHeightUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minHeight: `${numValue}%` },
                                });
                              } else {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minHeight: numValue },
                                });
                              }
                              setLocalMinHeight(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.size.minHeight === undefined) return "auto";
                            if (
                              typeof selectedComponent.size.minHeight === "string" &&
                              selectedComponent.size.minHeight.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localMinHeight;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minHeight: `${numValue}%` },
                                  });
                                  setMinHeightUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, minHeight: numValue },
                                  });
                                  setMinHeightUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minHeight: "10%" },
                                });
                                setLocalMinHeight("10");
                                setMinHeightUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, minHeight: 10 },
                                });
                                setLocalMinHeight("10");
                                setMinHeightUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.size.minHeight === undefined) return "-";
                                  if (
                                    typeof selectedComponent.size.minHeight === "string" &&
                                    selectedComponent.size.minHeight.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 order-2">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.maxWidth !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, maxWidth: undefined },
                          });
                          setLocalMaxWidth("");
                        }
                      }}
                    >
                      Max Width
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localMaxWidth}
                        onChange={(e) => setLocalMaxWidth(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                size: { ...selectedComponent.size, maxWidth: undefined },
                              });
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (maxWidthUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxWidth: `${numValue}%` },
                                  });
                                } else {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxWidth: numValue },
                                  });
                                }
                                setLocalMaxWidth(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localMaxWidth;

                          if (value === "") {
                            handleUpdate({
                              size: { ...selectedComponent.size, maxWidth: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (maxWidthUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxWidth: `${numValue}%` },
                                });
                              } else {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxWidth: numValue },
                                });
                              }
                              setLocalMaxWidth(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.size.maxWidth === undefined) return "auto";
                            if (
                              typeof selectedComponent.size.maxWidth === "string" &&
                              selectedComponent.size.maxWidth.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localMaxWidth;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxWidth: `${numValue}%` },
                                  });
                                  setMaxWidthUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxWidth: numValue },
                                  });
                                  setMaxWidthUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxWidth: "10%" },
                                });
                                setLocalMaxWidth("10");
                                setMaxWidthUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxWidth: 10 },
                                });
                                setLocalMaxWidth("10");
                                setMaxWidthUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.size.maxWidth === undefined) return "-";
                                  if (
                                    typeof selectedComponent.size.maxWidth === "string" &&
                                    selectedComponent.size.maxWidth.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 order-4">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.maxHeight !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, maxHeight: undefined },
                          });
                          setLocalMaxHeight("");
                        }
                      }}
                    >
                      Max Height
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localMaxHeight}
                        onChange={(e) => setLocalMaxHeight(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                size: { ...selectedComponent.size, maxHeight: undefined },
                              });
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (maxHeightUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxHeight: `${numValue}%` },
                                  });
                                } else {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxHeight: numValue },
                                  });
                                }
                                setLocalMaxHeight(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localMaxHeight;

                          if (value === "") {
                            handleUpdate({
                              size: { ...selectedComponent.size, maxHeight: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (maxHeightUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxHeight: `${numValue}%` },
                                });
                              } else {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxHeight: numValue },
                                });
                              }
                              setLocalMaxHeight(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.size.maxHeight === undefined) return "auto";
                            if (
                              typeof selectedComponent.size.maxHeight === "string" &&
                              selectedComponent.size.maxHeight.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localMaxHeight;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxHeight: `${numValue}%` },
                                  });
                                  setMaxHeightUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    size: { ...selectedComponent.size, maxHeight: numValue },
                                  });
                                  setMaxHeightUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxHeight: "10%" },
                                });
                                setLocalMaxHeight("10");
                                setMaxHeightUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  size: { ...selectedComponent.size, maxHeight: 10 },
                                });
                                setLocalMaxHeight("10");
                                setMaxHeightUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.size.maxHeight === undefined) return "-";
                                  if (
                                    typeof selectedComponent.size.maxHeight === "string" &&
                                    selectedComponent.size.maxHeight.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 order-5">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.size.aspectRatio !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            size: { ...selectedComponent.size, aspectRatio: undefined },
                          });
                          setLocalAspectRatio("");
                        }
                      }}
                    >
                      Aspect Ratio
                    </Label>
                  </div>
                  <div className="space-y-1 order-6">
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localAspectRatio}
                        onChange={(e) => setLocalAspectRatio(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.currentTarget.blur();
                            // Use requestAnimationFrame to ensure DOM updates complete
                            requestAnimationFrame(() => {
                              if (e.currentTarget && document.contains(e.currentTarget)) {
                                e.currentTarget.focus();
                                e.currentTarget.select();
                              }
                            });
                          }
                        }}
                        onBlur={() => {
                          const value = localAspectRatio;

                          if (value === "") {
                            handleUpdate({
                              size: { ...selectedComponent.size, aspectRatio: undefined },
                            });
                          } else {
                            handleUpdate({
                              size: { ...selectedComponent.size, aspectRatio: value },
                            });
                          }
                        }}
                        placeholder="e.g., 16/9, 4/3, 1/1"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {selectedComponent.type === "tabs" && (
                <div className="mt-3">
                  <h3 className="text-xs font-bold text-gray-300 pl-0.5">Tabs</h3>
                  <div className="space-y-1">
                    {tabsList.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-[10px] leading-none text-gray-400">
                          Tab Labels
                        </Label>
                        {tabsList.map((tab, index) => (
                          <Input
                            key={tab.id}
                            variant="compact-wide"
                            type="text"
                            value={tab.label}
                            placeholder={`Tab ${index + 1}`}
                            onChange={(e) => {
                              const nextTabs = tabsList.map((entry) =>
                                entry.id === tab.id
                                  ? { ...entry, label: e.target.value }
                                  : entry,
                              );
                              handleUpdate({
                                content: {
                                  ...selectedComponent.content,
                                  tabs: nextTabs,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                        ))}
                      </div>
                    )}
                    <ColorPicker
                      value={(tabStyle.textColor as string) || "#FFFFFFCC"}
                      onChange={(color) =>
                        handleUpdate({
                          content: {
                            ...selectedComponent.content,
                            tabStyle: { ...tabStyle, textColor: color },
                          },
                        })
                      }
                      label="Tab Text Color"
                      labelClassName="text-xs p-0.5 rounded mb-0.5 cursor-pointer text-gray-400"
                      variant="compact"
                      tightSpacing={true}
                    />
                    <ColorPicker
                      value={(tabStyle.activeTextColor as string) || "#ffffff"}
                      onChange={(color) =>
                        handleUpdate({
                          content: {
                            ...selectedComponent.content,
                            tabStyle: { ...tabStyle, activeTextColor: color },
                          },
                        })
                      }
                      label="Active Text Color"
                      labelClassName="text-xs p-0.5 rounded mb-0.5 cursor-pointer text-gray-400"
                      variant="compact"
                      tightSpacing={true}
                    />
                    <ColorPicker
                      value={(tabStyle.backgroundColor as string) || "#FFFFFF0F"}
                      onChange={(color) =>
                        handleUpdate({
                          content: {
                            ...selectedComponent.content,
                            tabStyle: { ...tabStyle, backgroundColor: color },
                          },
                        })
                      }
                      label="Tab Background"
                      labelClassName="text-xs p-0.5 rounded mb-0.5 cursor-pointer text-gray-400"
                      variant="compact"
                      includeOpacity={true}
                      tightSpacing={true}
                    />
                    <ColorPicker
                      value={
                        (tabStyle.activeBackgroundColor as string) || "#3B82F64D"
                      }
                      onChange={(color) =>
                        handleUpdate({
                          content: {
                            ...selectedComponent.content,
                            tabStyle: { ...tabStyle, activeBackgroundColor: color },
                          },
                        })
                      }
                      label="Active Background"
                      labelClassName="text-xs p-0.5 rounded mb-0.5 cursor-pointer text-gray-400"
                      variant="compact"
                      includeOpacity={true}
                      tightSpacing={true}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-0.5">
                        <Label className="text-[10px] leading-none text-gray-400">
                          Font Size
                        </Label>
                        <Input
                          variant="compact-wide"
                          type="number"
                          min="8"
                          step="1"
                          value={String((tabStyle.fontSize as number) ?? 12)}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            handleUpdate({
                              content: {
                                ...selectedComponent.content,
                                tabStyle: {
                                  ...tabStyle,
                                  fontSize: Number.isNaN(value) ? 12 : value,
                                },
                              },
                            });
                          }}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[10px] leading-none text-gray-400">
                          Font Weight
                        </Label>
                        <Input
                          variant="compact-wide"
                          type="number"
                          min="100"
                          step="100"
                          value={String((tabStyle.fontWeight as number) ?? 500)}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            handleUpdate({
                              content: {
                                ...selectedComponent.content,
                                tabStyle: {
                                  ...tabStyle,
                                  fontWeight: Number.isNaN(value) ? 500 : value,
                                },
                              },
                            });
                          }}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-0.5">
                        <Label className="text-[10px] leading-none text-gray-400">
                          Padding X
                        </Label>
                        <Input
                          variant="compact-wide"
                          type="number"
                          min="0"
                          step="1"
                          value={String((tabStyle.paddingX as number) ?? 8)}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            handleUpdate({
                              content: {
                                ...selectedComponent.content,
                                tabStyle: {
                                  ...tabStyle,
                                  paddingX: Number.isNaN(value) ? 8 : value,
                                },
                              },
                            });
                          }}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[10px] leading-none text-gray-400">
                          Padding Y
                        </Label>
                        <Input
                          variant="compact-wide"
                          type="number"
                          min="0"
                          step="1"
                          value={String((tabStyle.paddingY as number) ?? 4)}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            handleUpdate({
                              content: {
                                ...selectedComponent.content,
                                tabStyle: {
                                  ...tabStyle,
                                  paddingY: Number.isNaN(value) ? 4 : value,
                                },
                              },
                            });
                          }}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[10px] leading-none text-gray-400">Radius</Label>
                        <Input
                          variant="compact-wide"
                          type="number"
                          min="0"
                          step="1"
                          value={String((tabStyle.borderRadius as number) ?? 6)}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            handleUpdate({
                              content: {
                                ...selectedComponent.content,
                                tabStyle: {
                                  ...tabStyle,
                                  borderRadius: Number.isNaN(value) ? 6 : value,
                                },
                              },
                            });
                          }}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />

              {/* Position */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Position</h3>

                <div className="grid grid-cols-4 gap-2">
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.position.left !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            position: { ...selectedComponent.position, left: undefined },
                          });
                          setLocalLeft("");
                        }
                      }}
                    >
                      Left
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localLeft === "" ? "" : localLeft}
                        onChange={(e) => setLocalLeft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                position: { ...selectedComponent.position, left: undefined },
                              });
                              setLocalLeft("");
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (leftUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      left: `${numValue}%`,
                                      right: undefined,
                                    },
                                  });
                                } else {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      left: numValue,
                                      right: undefined,
                                    },
                                  });
                                }
                                setLocalLeft(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localLeft;
                          if (value === "") {
                            handleUpdate({
                              position: { ...selectedComponent.position, left: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (leftUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    left: `${numValue}%`,
                                    right: undefined,
                                  },
                                });
                              } else {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    left: numValue,
                                    right: undefined,
                                  },
                                });
                              }
                              // Update local state to show only the number
                              setLocalLeft(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.position.left === undefined) return "auto";
                            if (
                              typeof selectedComponent.position.left === "string" &&
                              selectedComponent.position.left.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localLeft;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      left: `${numValue}%`,
                                      right: undefined,
                                    },
                                  });
                                  setLeftUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      left: numValue,
                                      right: undefined,
                                    },
                                  });
                                  setLeftUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    left: "10%",
                                    right: undefined,
                                  },
                                });
                                setLocalLeft("10");
                                setLeftUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    left: 10,
                                    right: undefined,
                                  },
                                });
                                setLocalLeft("10");
                                setLeftUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.position.left === undefined) return "-";
                                  if (
                                    typeof selectedComponent.position.left === "string" &&
                                    selectedComponent.position.left.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.position.right !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            position: { ...selectedComponent.position, right: undefined },
                          });
                          setLocalRight("");
                        }
                      }}
                    >
                      Right
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localRight === "" ? "" : localRight}
                        onChange={(e) => setLocalRight(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                position: { ...selectedComponent.position, right: undefined },
                              });
                              setLocalRight("");
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (rightUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      right: `${numValue}%`,
                                      left: undefined,
                                    },
                                  });
                                } else {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      right: numValue,
                                      left: undefined,
                                    },
                                  });
                                }
                                setLocalRight(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localRight;
                          if (value === "") {
                            handleUpdate({
                              position: { ...selectedComponent.position, right: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (rightUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    right: `${numValue}%`,
                                    left: undefined,
                                  },
                                });
                              } else {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    right: numValue,
                                    left: undefined,
                                  },
                                });
                              }
                              // Update local state to show only the number
                              setLocalRight(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.position.right === undefined) return "auto";
                            if (
                              typeof selectedComponent.position.right === "string" &&
                              selectedComponent.position.right.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localRight;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      right: `${numValue}%`,
                                      left: undefined,
                                    },
                                  });
                                  setRightUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      right: numValue,
                                      left: undefined,
                                    },
                                  });
                                  setRightUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    right: "10%",
                                    left: undefined,
                                  },
                                });
                                setLocalRight("10");
                                setRightUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    right: 10,
                                    left: undefined,
                                  },
                                });
                                setLocalRight("10");
                                setRightUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.position.right === undefined) return "-";
                                  if (
                                    typeof selectedComponent.position.right === "string" &&
                                    selectedComponent.position.right.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.position.top !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            position: { ...selectedComponent.position, top: undefined },
                          });
                          setLocalTop("");
                        }
                      }}
                    >
                      Top
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localTop === "" ? "" : localTop}
                        onChange={(e) => setLocalTop(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                position: { ...selectedComponent.position, top: undefined },
                              });
                              setLocalTop("");
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (topUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      top: `${numValue}%`,
                                      bottom: undefined,
                                    },
                                  });
                                } else {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      top: numValue,
                                      bottom: undefined,
                                    },
                                  });
                                }
                                setLocalTop(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localTop;
                          if (value === "") {
                            handleUpdate({
                              position: { ...selectedComponent.position, top: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (topUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    top: `${numValue}%`,
                                    bottom: undefined,
                                  },
                                });
                              } else {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    top: numValue,
                                    bottom: undefined,
                                  },
                                });
                              }
                              // Update local state to show only the number
                              setLocalTop(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.position.top === undefined) return "auto";
                            if (
                              typeof selectedComponent.position.top === "string" &&
                              selectedComponent.position.top.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localTop;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      top: `${numValue}%`,
                                      bottom: undefined,
                                    },
                                  });
                                  setTopUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      top: numValue,
                                      bottom: undefined,
                                    },
                                  });
                                  setTopUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    top: "10%",
                                    bottom: undefined,
                                  },
                                });
                                setLocalTop("10");
                                setTopUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    top: 10,
                                    bottom: undefined,
                                  },
                                });
                                setLocalTop("10");
                                setTopUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.position.top === undefined) return "-";
                                  if (
                                    typeof selectedComponent.position.top === "string" &&
                                    selectedComponent.position.top.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.position.bottom !== undefined ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey) {
                          e.preventDefault();
                          handleUpdate({
                            position: { ...selectedComponent.position, bottom: undefined },
                          });
                          setLocalBottom("");
                        }
                      }}
                    >
                      Bottom
                    </Label>
                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="text"
                        value={localBottom === "" ? "" : localBottom}
                        onChange={(e) => setLocalBottom(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Call blur logic directly without blurring the field
                            const value = e.currentTarget.value;
                            if (value === "") {
                              handleUpdate({
                                position: { ...selectedComponent.position, bottom: undefined },
                              });
                              setLocalBottom("");
                            } else {
                              const numValue = parseFloat(value);
                              if (!Number.isNaN(numValue)) {
                                if (bottomUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      bottom: `${numValue}%`,
                                      top: undefined,
                                    },
                                  });
                                } else {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      bottom: numValue,
                                      top: undefined,
                                    },
                                  });
                                }
                                setLocalBottom(numValue.toString());
                              }
                            }
                            // Select all text for easy editing
                            e.currentTarget.select();
                          }
                        }}
                        onBlur={() => {
                          const value = localBottom;
                          if (value === "") {
                            handleUpdate({
                              position: { ...selectedComponent.position, bottom: undefined },
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!Number.isNaN(numValue)) {
                              if (bottomUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    bottom: `${numValue}%`,
                                    top: undefined,
                                  },
                                });
                              } else {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    bottom: numValue,
                                    top: undefined,
                                  },
                                });
                              }
                              // Update local state to show only the number
                              setLocalBottom(numValue.toString());
                            }
                          }
                        }}
                        placeholder="-"
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Select
                          value={(() => {
                            if (selectedComponent.position.bottom === undefined) return "auto";
                            if (
                              typeof selectedComponent.position.bottom === "string" &&
                              selectedComponent.position.bottom.endsWith("%")
                            )
                              return "%";
                            return "px";
                          })()}
                          onValueChange={(newUnit) => {
                            const currentValue = localBottom;
                            if (currentValue && currentValue !== "auto") {
                              const numValue = parseFloat(currentValue);
                              if (!Number.isNaN(numValue)) {
                                if (newUnit === "%") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      bottom: `${numValue}%`,
                                      top: undefined,
                                    },
                                  });
                                  setBottomUnit("%");
                                } else if (newUnit === "px") {
                                  handleUpdate({
                                    position: {
                                      ...selectedComponent.position,
                                      bottom: numValue,
                                      top: undefined,
                                    },
                                  });
                                  setBottomUnit("px");
                                }
                              }
                            } else {
                              // If current value is auto or empty, set to 10 with the new unit
                              if (newUnit === "%") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    bottom: "10%",
                                    top: undefined,
                                  },
                                });
                                setLocalBottom("10");
                                setBottomUnit("%");
                              } else if (newUnit === "px") {
                                handleUpdate({
                                  position: {
                                    ...selectedComponent.position,
                                    bottom: 10,
                                    top: undefined,
                                  },
                                });
                                setLocalBottom("10");
                                setBottomUnit("px");
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <SelectValue>
                              <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                {(() => {
                                  if (selectedComponent.position.bottom === undefined) return "-";
                                  if (
                                    typeof selectedComponent.position.bottom === "string" &&
                                    selectedComponent.position.bottom.endsWith("%")
                                  )
                                    return "%";
                                  return "px";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                            <SelectItem value="px">px</SelectItem>
                            <SelectItem value="%">%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />

              {/* Padding */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Padding</h3>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-0.5">
                      <Label
                        className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${
                          resolvedStyle.padding && resolvedStyle.padding !== 0
                            ? "text-blue-400 bg-blue-400/20"
                            : "text-gray-300"
                        }`}
                        onClick={(e) => {
                          if (e.altKey || e.metaKey) {
                            e.preventDefault();
                            setLocalPadding("");
                            setLocalPaddingTop("");
                            setLocalPaddingRight("");
                            setLocalPaddingBottom("");
                            setLocalPaddingLeft("");
                            handleUpdate({
                              style: {
                                ...resolvedStyle,
                                padding: 0,
                                paddingTop: 0,
                                paddingRight: 0,
                                paddingBottom: 0,
                                paddingLeft: 0,
                              },
                            });
                          }
                        }}
                      >
                        Padding
                      </Label>
                      {resolvedStyle.padding && resolvedStyle.padding !== 0 && (
                        <button
                          type="button"
                          className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
                          title="Clear padding"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setLocalPadding("");
                            setLocalPaddingTop("");
                            setLocalPaddingRight("");
                            setLocalPaddingBottom("");
                            setLocalPaddingLeft("");
                            handleUpdate({
                              style: {
                                ...resolvedStyle,
                                padding: 0,
                                paddingTop: 0,
                                paddingRight: 0,
                                paddingBottom: 0,
                                paddingLeft: 0,
                              },
                            });
                          }}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    {!showPaddingFields ? (
                      <div className="flex items-center space-x-2 w-full">
                        <div className="relative flex-1">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localPadding}
                            placeholder="-"
                            onChange={(e) => {
                              const value = e.target.value;
                              // If user types %, detect it and strip it from display
                              if (value.includes("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPadding(numValue.toString());
                                  setPaddingUnit("%");
                                }
                              } else {
                                setLocalPadding(value);
                                setPaddingUnit("px");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                if (value === "") {
                                  setLocalPadding("");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      padding: 0,
                                      paddingTop: 0,
                                      paddingRight: 0,
                                      paddingBottom: 0,
                                      paddingLeft: 0,
                                    },
                                  });
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      paddingUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        padding: paddingValue,
                                        paddingTop: paddingValue,
                                        paddingRight: paddingValue,
                                        paddingBottom: paddingValue,
                                        paddingLeft: paddingValue,
                                      },
                                    });
                                  }
                                }
                                e.currentTarget.select();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalPadding("");
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    padding: 0,
                                    paddingTop: 0,
                                    paddingRight: 0,
                                    paddingBottom: 0,
                                    paddingLeft: 0,
                                  },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const paddingValue =
                                    paddingUnit === "%" ? `${numValue}%` : numValue;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      padding: paddingValue,
                                      paddingTop: paddingValue,
                                      paddingRight: paddingValue,
                                      paddingBottom: paddingValue,
                                      paddingLeft: paddingValue,
                                    },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 w-full pr-12"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <Select
                              value={(() => {
                                if (
                                  resolvedStyle.padding === undefined ||
                                  resolvedStyle.padding === 0
                                )
                                  return "auto";
                                if (
                                  typeof resolvedStyle.padding === "string" &&
                                  resolvedStyle.padding.endsWith("%")
                                )
                                  return "%";
                                return "px";
                              })()}
                              onValueChange={(newUnit) => {
                                const currentValue = localPadding;
                                if (currentValue && currentValue !== "auto") {
                                  const numValue = parseFloat(currentValue);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      newUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        padding: paddingValue,
                                        paddingTop: paddingValue,
                                        paddingRight: paddingValue,
                                        paddingBottom: paddingValue,
                                        paddingLeft: paddingValue,
                                      },
                                    });
                                    setPaddingUnit(newUnit as "px" | "%");
                                  }
                                } else {
                                  // If current value is auto or empty, set to 10 with the new unit
                                  const paddingValue = newUnit === "%" ? "10%" : 10;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      padding: paddingValue,
                                      paddingTop: paddingValue,
                                      paddingRight: paddingValue,
                                      paddingBottom: paddingValue,
                                      paddingLeft: paddingValue,
                                    },
                                  });
                                  setLocalPadding("10");
                                  setPaddingUnit(newUnit as "px" | "%");
                                }
                              }}
                            >
                              <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                                <SelectValue>
                                  <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                    {(() => {
                                      if (
                                        resolvedStyle.padding === undefined ||
                                        resolvedStyle.padding === 0
                                      )
                                        return "-";
                                      if (
                                        typeof resolvedStyle.padding === "string" &&
                                        resolvedStyle.padding.endsWith("%")
                                      )
                                        return "%";
                                      return "px";
                                    })()}
                                  </span>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                                <SelectItem value="px">px</SelectItem>
                                <SelectItem value="%">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPaddingFields(!showPaddingFields)}
                          className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                          title="Toggle individual padding fields"
                        >
                          ▢
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowPaddingFields(!showPaddingFields)}
                        className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                        title="Toggle individual padding fields"
                      >
                        ▢
                      </button>
                    )}
                  </div>

                  {showPaddingFields && (
                    <div className="col-span-2 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <div className="flex items-center gap-0.5">
                          <Label
                            className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                              resolvedStyle.paddingTop &&
                              resolvedStyle.paddingTop !== 0
                                ? "text-blue-400 bg-blue-400/20"
                                : "text-gray-400"
                            }`}
                            onClick={(e) => {
                              if (e.altKey || e.metaKey) {
                                e.preventDefault();
                                setLocalPaddingTop("0");
                                handleUpdate({
                                  style: { ...resolvedStyle, paddingTop: 0 },
                                });
                              }
                            }}
                          >
                            ↑ Top
                          </Label>
                          {resolvedStyle.paddingTop &&
                            resolvedStyle.paddingTop !== 0 && (
                              <button
                                type="button"
                                className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
                                title="Clear top padding"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setLocalPaddingTop("0");
                                  handleUpdate({
                                    style: { ...resolvedStyle, paddingTop: 0 },
                                  });
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                        </div>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localPaddingTop}
                            placeholder="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              // If user types %, detect it and strip it from display
                              if (value.includes("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingTop(numValue.toString());
                                  setPaddingTopUnit("%");
                                }
                              } else {
                                setLocalPaddingTop(value);
                                setPaddingTopUnit("px");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                if (value === "") {
                                  setLocalPaddingTop("");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingTop: 0,
                                    },
                                  });
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      paddingTopUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingTop: paddingValue,
                                      },
                                    });
                                  }
                                }
                                e.currentTarget.select();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalPaddingTop("");
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    paddingTop: 0,
                                  },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const paddingValue =
                                    paddingTopUnit === "%" ? `${numValue}%` : numValue;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingTop: paddingValue,
                                    },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white text-xs pr-8"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <Select
                              value={(() => {
                                if (
                                  resolvedStyle.paddingTop === undefined ||
                                  resolvedStyle.paddingTop === 0
                                )
                                  return "auto";
                                if (
                                  typeof resolvedStyle.paddingTop === "string" &&
                                  resolvedStyle.paddingTop.endsWith("%")
                                )
                                  return "%";
                                return "px";
                              })()}
                              onValueChange={(newUnit) => {
                                const currentValue = localPaddingTop;
                                if (currentValue && currentValue !== "auto") {
                                  const numValue = parseFloat(currentValue);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      newUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingTop: paddingValue,
                                      },
                                    });
                                    setPaddingTopUnit(newUnit as "px" | "%");
                                  }
                                } else {
                                  // If current value is auto or empty, set to 10 with the new unit
                                  const paddingValue = newUnit === "%" ? "10%" : 10;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingTop: paddingValue,
                                    },
                                  });
                                  setLocalPaddingTop("10");
                                  setPaddingTopUnit(newUnit as "px" | "%");
                                }
                              }}
                            >
                              <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                                <SelectValue>
                                  <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                    {(() => {
                                      if (
                                        resolvedStyle.paddingTop === undefined ||
                                        resolvedStyle.paddingTop === 0
                                      )
                                        return "-";
                                      if (
                                        typeof resolvedStyle.paddingTop === "string" &&
                                        resolvedStyle.paddingTop.endsWith("%")
                                      )
                                        return "%";
                                      return "px";
                                    })()}
                                  </span>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                                <SelectItem value="px">px</SelectItem>
                                <SelectItem value="%">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-0.5">
                          <Label
                            className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                              resolvedStyle.paddingRight &&
                              resolvedStyle.paddingRight !== 0
                                ? "text-blue-400 bg-blue-400/20"
                                : "text-gray-400"
                            }`}
                            onClick={(e) => {
                              if (e.altKey || e.metaKey) {
                                e.preventDefault();
                                setLocalPaddingRight("0");
                                handleUpdate({
                                  style: { ...resolvedStyle, paddingRight: 0 },
                                });
                              }
                            }}
                          >
                            → Right
                          </Label>
                          {resolvedStyle.paddingRight &&
                            resolvedStyle.paddingRight !== 0 && (
                              <button
                                type="button"
                                className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
                                title="Clear right padding"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setLocalPaddingRight("0");
                                  handleUpdate({
                                    style: { ...resolvedStyle, paddingRight: 0 },
                                  });
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                        </div>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localPaddingRight}
                            placeholder="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              // If user types %, detect it and strip it from display
                              if (value.includes("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingRight(numValue.toString());
                                  setPaddingRightUnit("%");
                                }
                              } else {
                                setLocalPaddingRight(value);
                                setPaddingRightUnit("px");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                if (value === "") {
                                  setLocalPaddingRight("");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingRight: 0,
                                    },
                                  });
                                } else if (value.endsWith("%")) {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    setLocalPaddingRight(numValue.toString());
                                    setPaddingRightUnit("%");
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingRight: `${numValue}%`,
                                      },
                                    });
                                  }
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    setLocalPaddingRight(numValue.toString());
                                    setPaddingRightUnit("px");
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingRight: numValue,
                                      },
                                    });
                                  }
                                }
                                e.currentTarget.select();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalPaddingRight("");
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    paddingRight: 0,
                                  },
                                });
                              } else if (value.endsWith("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingRight(numValue.toString());
                                  setPaddingRightUnit("%");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingRight: `${numValue}%`,
                                    },
                                  });
                                }
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingRight(numValue.toString());
                                  setPaddingRightUnit("px");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingRight: numValue,
                                    },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white text-xs pr-8"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <Select
                              value={(() => {
                                if (
                                  resolvedStyle.paddingRight === undefined ||
                                  resolvedStyle.paddingRight === 0
                                )
                                  return "auto";
                                if (
                                  typeof resolvedStyle.paddingRight === "string" &&
                                  resolvedStyle.paddingRight.endsWith("%")
                                )
                                  return "%";
                                return "px";
                              })()}
                              onValueChange={(newUnit) => {
                                const currentValue = localPaddingRight;
                                if (currentValue && currentValue !== "auto") {
                                  const numValue = parseFloat(currentValue);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      newUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingRight: paddingValue,
                                      },
                                    });
                                    setPaddingRightUnit(newUnit as "px" | "%");
                                  }
                                } else {
                                  // If current value is auto or empty, set to 10 with the new unit
                                  const paddingValue = newUnit === "%" ? "10%" : 10;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingRight: paddingValue,
                                    },
                                  });
                                  setLocalPaddingRight("10");
                                  setPaddingRightUnit(newUnit as "px" | "%");
                                }
                              }}
                            >
                              <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                                <SelectValue>
                                  <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                    {(() => {
                                      if (
                                        resolvedStyle.paddingRight === undefined ||
                                        resolvedStyle.paddingRight === 0
                                      )
                                        return "-";
                                      if (
                                        typeof resolvedStyle.paddingRight === "string" &&
                                        resolvedStyle.paddingRight.endsWith("%")
                                      )
                                        return "%";
                                      return "px";
                                    })()}
                                  </span>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                                <SelectItem value="px">px</SelectItem>
                                <SelectItem value="%">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-0.5">
                          <Label
                            className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                              resolvedStyle.paddingBottom &&
                              resolvedStyle.paddingBottom !== 0
                                ? "text-blue-400 bg-blue-400/20"
                                : "text-gray-400"
                            }`}
                            onClick={(e) => {
                              if (e.altKey || e.metaKey) {
                                e.preventDefault();
                                setLocalPaddingBottom("0");
                                handleUpdate({
                                  style: { ...resolvedStyle, paddingBottom: 0 },
                                });
                              }
                            }}
                          >
                            ↓ Bottom
                          </Label>
                          {resolvedStyle.paddingBottom &&
                            resolvedStyle.paddingBottom !== 0 && (
                              <button
                                type="button"
                                className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
                                title="Clear bottom padding"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setLocalPaddingBottom("0");
                                  handleUpdate({
                                    style: { ...resolvedStyle, paddingBottom: 0 },
                                  });
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                        </div>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localPaddingBottom}
                            placeholder="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              // If user types %, detect it and strip it from display
                              if (value.includes("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingBottom(numValue.toString());
                                  setPaddingBottomUnit("%");
                                }
                              } else {
                                setLocalPaddingBottom(value);
                                setPaddingBottomUnit("px");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                if (value === "") {
                                  setLocalPaddingBottom("");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingBottom: 0,
                                    },
                                  });
                                } else if (value.endsWith("%")) {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    setLocalPaddingBottom(numValue.toString());
                                    setPaddingBottomUnit("%");
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingBottom: `${numValue}%`,
                                      },
                                    });
                                  }
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    setLocalPaddingBottom(numValue.toString());
                                    setPaddingBottomUnit("px");
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingBottom: numValue,
                                      },
                                    });
                                  }
                                }
                                e.currentTarget.select();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalPaddingBottom("");
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    paddingBottom: 0,
                                  },
                                });
                              } else if (value.endsWith("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingBottom(numValue.toString());
                                  setPaddingBottomUnit("%");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingBottom: `${numValue}%`,
                                    },
                                  });
                                }
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingBottom(numValue.toString());
                                  setPaddingBottomUnit("px");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingBottom: numValue,
                                    },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white text-xs pr-8"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <Select
                              value={(() => {
                                if (
                                  resolvedStyle.paddingBottom === undefined ||
                                  resolvedStyle.paddingBottom === 0
                                )
                                  return "auto";
                                if (
                                  typeof resolvedStyle.paddingBottom === "string" &&
                                  resolvedStyle.paddingBottom.endsWith("%")
                                )
                                  return "%";
                                return "px";
                              })()}
                              onValueChange={(newUnit) => {
                                const currentValue = localPaddingBottom;
                                if (currentValue && currentValue !== "auto") {
                                  const numValue = parseFloat(currentValue);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      newUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingBottom: paddingValue,
                                      },
                                    });
                                    setPaddingBottomUnit(newUnit as "px" | "%");
                                  }
                                } else {
                                  // If current value is auto or empty, set to 10 with the new unit
                                  const paddingValue = newUnit === "%" ? "10%" : 10;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingBottom: paddingValue,
                                    },
                                  });
                                  setLocalPaddingBottom("10");
                                  setPaddingBottomUnit(newUnit as "px" | "%");
                                }
                              }}
                            >
                              <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                                <SelectValue>
                                  <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                    {(() => {
                                      if (
                                        resolvedStyle.paddingBottom === undefined ||
                                        resolvedStyle.paddingBottom === 0
                                      )
                                        return "-";
                                      if (
                                        typeof resolvedStyle.paddingBottom === "string" &&
                                        resolvedStyle.paddingBottom.endsWith("%")
                                      )
                                        return "%";
                                      return "px";
                                    })()}
                                  </span>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                                <SelectItem value="px">px</SelectItem>
                                <SelectItem value="%">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-0.5">
                          <Label
                            className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                              resolvedStyle.paddingLeft &&
                              resolvedStyle.paddingLeft !== 0
                                ? "text-blue-400 bg-blue-400/20"
                                : "text-gray-400"
                            }`}
                            onClick={(e) => {
                              if (e.altKey || e.metaKey) {
                                e.preventDefault();
                                setLocalPaddingLeft("0");
                                handleUpdate({
                                  style: { ...resolvedStyle, paddingLeft: 0 },
                                });
                              }
                            }}
                          >
                            ← Left
                          </Label>
                          {resolvedStyle.paddingLeft &&
                            resolvedStyle.paddingLeft !== 0 && (
                              <button
                                type="button"
                                className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
                                title="Clear left padding"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setLocalPaddingLeft("0");
                                  handleUpdate({
                                    style: { ...resolvedStyle, paddingLeft: 0 },
                                  });
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                        </div>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localPaddingLeft}
                            placeholder="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              // If user types %, detect it and strip it from display
                              if (value.includes("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingLeft(numValue.toString());
                                  setPaddingLeftUnit("%");
                                }
                              } else {
                                setLocalPaddingLeft(value);
                                setPaddingLeftUnit("px");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                if (value === "") {
                                  setLocalPaddingLeft("");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingLeft: 0,
                                    },
                                  });
                                } else if (value.endsWith("%")) {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    setLocalPaddingLeft(numValue.toString());
                                    setPaddingLeftUnit("%");
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingLeft: `${numValue}%`,
                                      },
                                    });
                                  }
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!Number.isNaN(numValue)) {
                                    setLocalPaddingLeft(numValue.toString());
                                    setPaddingLeftUnit("px");
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingLeft: numValue,
                                      },
                                    });
                                  }
                                }
                                e.currentTarget.select();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalPaddingLeft("");
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    paddingLeft: 0,
                                  },
                                });
                              } else if (value.endsWith("%")) {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingLeft(numValue.toString());
                                  setPaddingLeftUnit("%");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingLeft: `${numValue}%`,
                                    },
                                  });
                                }
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  setLocalPaddingLeft(numValue.toString());
                                  setPaddingLeftUnit("px");
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingLeft: numValue,
                                    },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white text-xs pr-8"
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <Select
                              value={(() => {
                                if (
                                  resolvedStyle.paddingLeft === undefined ||
                                  resolvedStyle.paddingLeft === 0
                                )
                                  return "auto";
                                if (
                                  typeof resolvedStyle.paddingLeft === "string" &&
                                  resolvedStyle.paddingLeft.endsWith("%")
                                )
                                  return "%";
                                return "px";
                              })()}
                              onValueChange={(newUnit) => {
                                const currentValue = localPaddingLeft;
                                if (currentValue && currentValue !== "auto") {
                                  const numValue = parseFloat(currentValue);
                                  if (!Number.isNaN(numValue)) {
                                    const paddingValue =
                                      newUnit === "%" ? `${numValue}%` : numValue;
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        paddingLeft: paddingValue,
                                      },
                                    });
                                    setPaddingLeftUnit(newUnit as "px" | "%");
                                  }
                                } else {
                                  // If current value is auto or empty, set to 10 with the new unit
                                  const paddingValue = newUnit === "%" ? "10%" : 10;
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      paddingLeft: paddingValue,
                                    },
                                  });
                                  setLocalPaddingLeft("10");
                                  setPaddingLeftUnit(newUnit as "px" | "%");
                                }
                              }}
                            >
                              <SelectTrigger className="h-auto p-0 border-0 bg-transparent shadow-none hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                                <SelectValue>
                                  <span className="text-gray-400 text-xs bg-transparent cursor-pointer">
                                    {(() => {
                                      if (
                                        resolvedStyle.paddingLeft === undefined ||
                                        resolvedStyle.paddingLeft === 0
                                      )
                                        return "-";
                                      if (
                                        typeof resolvedStyle.paddingLeft === "string" &&
                                        resolvedStyle.paddingLeft.endsWith("%")
                                      )
                                        return "%";
                                      return "px";
                                    })()}
                                  </span>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-600 w-auto min-w-0">
                                <SelectItem value="px">px</SelectItem>
                                <SelectItem value="%">%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Margin */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Margin</h3>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${
                        selectedComponent.margin &&
                        (selectedComponent.margin.top !== 0 ||
                          selectedComponent.margin.right !== 0 ||
                          selectedComponent.margin.bottom !== 0 ||
                          selectedComponent.margin.left !== 0)
                          ? "text-blue-400 bg-blue-400/20"
                          : "text-gray-300"
                      }`}
                      onClick={(e) => {
                        if (e.altKey || e.metaKey || e.ctrlKey) {
                          e.preventDefault();
                          setLocalMargin("");
                          setLocalMarginTop("");
                          setLocalMarginRight("");
                          setLocalMarginBottom("");
                          setLocalMarginLeft("");
                          handleUpdate({
                            margin: undefined,
                          });
                        }
                      }}
                    >
                      Margin
                    </Label>
                  </div>

                  <div className="flex items-center justify-end">
                    {!showMarginFields ? (
                      <div className="flex items-center space-x-2 w-full">
                        <div className="relative flex-1">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localMargin}
                            placeholder="0"
                            onChange={(e) => setLocalMargin(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                applyUniformMargin(e.currentTarget.value);
                              } else if (e.key === "Escape") {
                                const currentMargin = selectedComponent.margin;
                                if (currentMargin) {
                                  const isUniform =
                                    currentMargin.top === currentMargin.right &&
                                    currentMargin.top === currentMargin.bottom &&
                                    currentMargin.top === currentMargin.left;
                                  setLocalMargin(isUniform ? currentMargin.top.toString() : "");
                                } else {
                                  setLocalMargin("");
                                }
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => applyUniformMargin(e.currentTarget.value)}
                            className="bg-gray-700 border-gray-600 text-white pr-6"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            px
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowMarginFields(!showMarginFields)}
                          className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                          title="Toggle individual margin fields"
                        >
                          ▢
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowMarginFields(!showMarginFields)}
                        className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                        title="Toggle individual margin fields"
                      >
                        ▢
                      </button>
                    )}
                  </div>

                  {showMarginFields && (
                    <div className="col-span-2 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            selectedComponent.margin?.top !== undefined &&
                            selectedComponent.margin?.top !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalMarginTop("0");
                              const currentMargin = selectedComponent.margin || {
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                              };
                              handleUpdate({
                                margin: { ...currentMargin, top: 0 },
                              });
                            }
                          }}
                        >
                          ↑ Top
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localMarginTop}
                            placeholder="0"
                            onChange={(e) => setLocalMarginTop(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, top: numValue },
                                  });
                                }
                              } else if (e.key === "Escape") {
                                setLocalMarginTop(
                                  selectedComponent.margin?.top?.toString() || "",
                                );
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalMarginTop("");
                                const currentMargin = selectedComponent.margin || {
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  left: 0,
                                };
                                handleUpdate({
                                  margin: { ...currentMargin, top: 0 },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, top: numValue },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-6"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            px
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            selectedComponent.margin?.right !== undefined &&
                            selectedComponent.margin?.right !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalMarginRight("0");
                              const currentMargin = selectedComponent.margin || {
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                              };
                              handleUpdate({
                                margin: { ...currentMargin, right: 0 },
                              });
                            }
                          }}
                        >
                          → Right
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localMarginRight}
                            placeholder="0"
                            onChange={(e) => setLocalMarginRight(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, right: numValue },
                                  });
                                }
                              } else if (e.key === "Escape") {
                                setLocalMarginRight(
                                  selectedComponent.margin?.right?.toString() || "",
                                );
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalMarginRight("");
                                const currentMargin = selectedComponent.margin || {
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  left: 0,
                                };
                                handleUpdate({
                                  margin: { ...currentMargin, right: 0 },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, right: numValue },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-6"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            px
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            selectedComponent.margin?.bottom !== undefined &&
                            selectedComponent.margin?.bottom !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalMarginBottom("0");
                              const currentMargin = selectedComponent.margin || {
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                              };
                              handleUpdate({
                                margin: { ...currentMargin, bottom: 0 },
                              });
                            }
                          }}
                        >
                          ↓ Bottom
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localMarginBottom}
                            placeholder="0"
                            onChange={(e) => setLocalMarginBottom(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, bottom: numValue },
                                  });
                                }
                              } else if (e.key === "Escape") {
                                setLocalMarginBottom(
                                  selectedComponent.margin?.bottom?.toString() || "",
                                );
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalMarginBottom("");
                                const currentMargin = selectedComponent.margin || {
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  left: 0,
                                };
                                handleUpdate({
                                  margin: { ...currentMargin, bottom: 0 },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, bottom: numValue },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-6"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            px
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            selectedComponent.margin?.left !== undefined &&
                            selectedComponent.margin?.left !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalMarginLeft("0");
                              const currentMargin = selectedComponent.margin || {
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                              };
                              handleUpdate({
                                margin: { ...currentMargin, left: 0 },
                              });
                            }
                          }}
                        >
                          ← Left
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            value={localMarginLeft}
                            placeholder="0"
                            onChange={(e) => setLocalMarginLeft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const value = e.currentTarget.value;
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, left: numValue },
                                  });
                                }
                              } else if (e.key === "Escape") {
                                setLocalMarginLeft(
                                  selectedComponent.margin?.left?.toString() || "",
                                );
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.currentTarget.value;
                              if (value === "") {
                                setLocalMarginLeft("");
                                const currentMargin = selectedComponent.margin || {
                                  top: 0,
                                  right: 0,
                                  bottom: 0,
                                  left: 0,
                                };
                                handleUpdate({
                                  margin: { ...currentMargin, left: 0 },
                                });
                              } else {
                                const numValue = parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                  const currentMargin = selectedComponent.margin || {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  };
                                  handleUpdate({
                                    margin: { ...currentMargin, left: numValue },
                                  });
                                }
                              }
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-6"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            px
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />

              {/* Content Alignment - Box only */}
              {selectedComponent.type === "box" && (
                <>
                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">Content Alignment</h3>
                    <div className="flex items-center gap-3 mt-1">
                      {/* 3×3 alignment grid
                          Rows → justifyContent (main axis: top/center/bottom)
                          Cols → alignItems    (cross axis: left/center/right)
                          Each cell previews 3 stacked bars at that position */}
                      {(() => {
                        const flexDir =
                          (resolvedStyle.flexDirection as string) || "column";
                        const curJustify =
                          (resolvedStyle.justifyContent as string) || "flex-start";
                        const curAlign =
                          (resolvedStyle.alignItems as string) || "center";
                        const axisValues = ["flex-start", "center", "flex-end"] as const;
                        const axisLabels = ["Top", "Middle", "Bottom"] as const;
                        const crossLabels = ["Left", "Center", "Right"] as const;
                        const isRow = flexDir === "row";

                        const cells: { justify: string; align: string; label: string }[] = [];
                        axisValues.forEach((rowValue, rowIdx) => {
                          axisValues.forEach((colValue, colIdx) => {
                            const justify = isRow ? colValue : rowValue;
                            const align = isRow ? rowValue : colValue;
                            cells.push({
                              justify,
                              align,
                              label: `${axisLabels[rowIdx]} ${crossLabels[colIdx]}`,
                            });
                          });
                        });
                        return (
                          <div className="grid grid-cols-3 gap-px bg-gray-600 border border-gray-600 rounded overflow-hidden">
                            {cells.map(({ justify, align, label }) => {
                              const isSelected =
                                curJustify === justify && curAlign === align;
                              // Inner preview: 3 bars laid out with this cell's flex values
                              // For column direction: justifyContent = vertical, alignItems = horizontal
                              // For row direction:    justifyContent = horizontal, alignItems = vertical
                              const innerJustify = justify;
                              const innerAlign = align;
                              return (
                                <button
                                  key={label}
                                  type="button"
                                  title={label}
                                  onClick={() =>
                                    handleUpdate({
                                      style: {
                                        ...resolvedStyle,
                                        justifyContent: justify,
                                        alignItems: align,
                                      },
                                    })
                                  }
                                  className={`w-7 h-7 ${isSelected ? "bg-blue-500/40 text-blue-300" : "bg-gray-700 text-gray-500 hover:bg-gray-600 hover:text-gray-300"}`}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: flexDir as React.CSSProperties["flexDirection"],
                                      justifyContent: innerJustify,
                                      alignItems: innerAlign,
                                      width: "100%",
                                      height: "100%",
                                      gap: "1.5px",
                                      padding: "3px",
                                      boxSizing: "border-box",
                                    }}
                                  >
                                    {[0, 1, 2].map((i) => (
                                      <div
                                        key={i}
                                        style={{
                                          width: flexDir === "column" ? "10px" : "2px",
                                          height: flexDir === "column" ? "2px" : "10px",
                                          borderRadius: "1px",
                                          backgroundColor: "currentColor",
                                          flexShrink: 0,
                                        }}
                                      />
                                    ))}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Direction toggle: column / row */}
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          title="Column (stack vertically)"
                          onClick={() =>
                            handleUpdate({
                              style: { ...resolvedStyle, flexDirection: "column" },
                            })
                          }
                          className={`w-7 h-7 flex items-center justify-center rounded ${
                            ((resolvedStyle.flexDirection as string) || "column") ===
                            "column"
                              ? "bg-blue-500/40 text-blue-300"
                              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                          }`}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          title="Row (stack horizontally)"
                          onClick={() =>
                            handleUpdate({
                              style: { ...resolvedStyle, flexDirection: "row" },
                            })
                          }
                          className={`w-7 h-7 flex items-center justify-center rounded ${
                            (resolvedStyle.flexDirection as string) === "row"
                              ? "bg-blue-500/40 text-blue-300"
                              : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                          }`}
                        >
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Gap controls */}
                    <div className="flex items-end gap-1 mt-1.5">
                      {/* Horizontal Gap */}
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs p-0.5 rounded text-gray-400">Horizontal Gap</Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            placeholder="-"
                            value={
                              gapLinked
                                ? String(
                                    parseFloat(
                                      String(
                                        resolvedStyle.columnGap ??
                                          resolvedStyle.gap ??
                                          "",
                                      ),
                                    ) || ""
                                  )
                                : String(
                                    parseFloat(String(resolvedStyle.columnGap ?? "")) ||
                                      ""
                                  )
                            }
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  columnGap: Number.isNaN(v) ? undefined : v,
                                  ...(gapLinked
                                    ? { rowGap: Number.isNaN(v) ? undefined : v, gap: undefined }
                                    : {}),
                                },
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.currentTarget.select();
                            }}
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px] pointer-events-none">
                            px
                          </div>
                        </div>
                      </div>

                      {/* Link toggle */}
                      <button
                        type="button"
                        title={gapLinked ? "Unlink gaps" : "Link gaps"}
                        onClick={() => setGapLinked((v) => !v)}
                        className={`mb-1 p-0.5 rounded ${gapLinked ? "text-blue-400 hover:text-blue-300" : "text-gray-500 hover:text-gray-300"}`}
                      >
                        {gapLinked ? (
                          <Link2 className="w-3 h-3" />
                        ) : (
                          <Unlink2 className="w-3 h-3" />
                        )}
                      </button>

                      {/* Vertical Gap */}
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs p-0.5 rounded text-gray-400">Vertical Gap</Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="text"
                            placeholder="-"
                            value={
                              gapLinked
                                ? String(
                                    parseFloat(
                                      String(
                                        resolvedStyle.rowGap ??
                                          resolvedStyle.gap ??
                                          "",
                                      ),
                                    ) || ""
                                  )
                                : String(
                                    parseFloat(String(resolvedStyle.rowGap ?? "")) || ""
                                  )
                            }
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  rowGap: Number.isNaN(v) ? undefined : v,
                                  ...(gapLinked
                                    ? { columnGap: Number.isNaN(v) ? undefined : v, gap: undefined }
                                    : {}),
                                },
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.currentTarget.select();
                            }}
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px] pointer-events-none">
                            px
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Background */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Background</h3>

                <div className="space-y-1">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <ColorPicker
                          value={resolvedStyle.backgroundColor || "#FFFFFF54"}
                          onChange={(color) =>
                            handleUpdate({
                              style: { ...resolvedStyle, backgroundColor: color },
                            })
                          }
                          label="Background Color"
                          labelClassName={
                            resolvedStyle.backgroundColor &&
                            resolvedStyle.backgroundColor !== "#FFFFFF54"
                              ? "text-xs p-0.5 rounded mb-1 cursor-pointer text-blue-400 bg-blue-400/20"
                              : "text-xs p-0.5 rounded mb-1 cursor-pointer text-gray-400"
                          }
                          variant="compact"
                          onReset={() =>
                            handleUpdate({
                              style: {
                                ...resolvedStyle,
                                backgroundColor: "#FFFFFF54",
                              },
                            })
                          }
                          includeOpacity={true}
                          opacityValue={(() => {
                            // Extract opacity from rgba value or use default
                            const bgColor = resolvedStyle.backgroundColor;
                            if (bgColor && bgColor.startsWith("color-mix(")) {
                              const match = bgColor.match(
                                /color-mix\([^,]+,\s*[^,]+\s+([0-9.]+)%\s*,\s*transparent\s*\)/i,
                              );
                              if (match) {
                                const percent = parseFloat(match[1]);
                                if (Number.isFinite(percent)) return Math.max(0, Math.min(1, percent / 100));
                              }
                            }
                            if (bgColor?.startsWith("#") && bgColor.length === 9) {
                              const alpha = parseInt(bgColor.slice(7, 9), 16);
                              return Number.isNaN(alpha) ? 0.33 : alpha / 255;
                            }
                            if (bgColor?.startsWith("rgba(")) {
                              const match = bgColor.match(
                                /rgba?\([^,]+,\s*[^,]+,\s*[^,]+,\s*([^)]+)\)/,
                              );
                              if (match) {
                                return parseFloat(match[1]);
                              }
                            }
                            return 0.33; // Default opacity
                          })()}
                          onOpacityChange={(opacity) => {
                            // Combine current color with new opacity
                            const currentColor =
                              resolvedStyle.backgroundColor || "#000000";
                            let baseColor = currentColor;
                            const colorMixMatch = currentColor.match(
                              /color-mix\([^,]+,\s*([^,]+)\s+[0-9.]+%\s*,\s*transparent\s*\)/i,
                            );
                            if (colorMixMatch) {
                              baseColor = colorMixMatch[1].trim();
                            }

                            // Extract base color (without alpha)
                            if (currentColor.startsWith("rgba(")) {
                              const match = currentColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
                              if (match) {
                                const [, r, g, b] = match; // Skip first element (full match)
                                baseColor = `rgb(${r}, ${g}, ${b})`;
                              }
                            } else if (currentColor.startsWith("rgb(")) {
                              baseColor = currentColor;
                            } else if (currentColor.startsWith("#")) {
                              baseColor = currentColor.slice(0, 7);
                            }

                            const clampOpacity = Math.min(1, Math.max(0, opacity));
                            const toHex = (value: number) =>
                              Math.round(value).toString(16).padStart(2, "0").toUpperCase();

                            // Create new hex value with alpha
                            let newColor: string | undefined;
                            if (
                              !(baseColor.startsWith("#") || baseColor.startsWith("rgb(")) &&
                              !baseColor.startsWith("rgba(") &&
                              !baseColor.startsWith("var(") &&
                              !baseColor.startsWith("--")
                            ) {
                              baseColor = toCSSVarReference(baseColor);
                            }
                            if (baseColor.startsWith("var(") || baseColor.startsWith("--")) {
                              const percent = Math.round(clampOpacity * 100);
                              newColor = `color-mix(in srgb, ${baseColor} ${percent}%, transparent)`;
                            } else if (baseColor.startsWith("#")) {
                              const r = parseInt(baseColor.slice(1, 3), 16);
                              const g = parseInt(baseColor.slice(3, 5), 16);
                              const b = parseInt(baseColor.slice(5, 7), 16);
                              const alphaHex = toHex(clampOpacity * 255);
                              newColor = `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
                            } else if (baseColor.startsWith("rgb(")) {
                              const match = baseColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)/);
                              if (match) {
                                const [, r, g, b] = match; // Skip first element (full match)
                                const alphaHex = toHex(clampOpacity * 255);
                                newColor = `#${toHex(Number(r))}${toHex(Number(g))}${toHex(
                                  Number(b),
                                )}${alphaHex}`;
                              }
                            } else {
                              newColor = baseColor;
                            }

                            if (newColor) {
                              handleUpdate({
                                style: { ...resolvedStyle, backgroundColor: newColor },
                              });
                            }
                          }}
                        />
                      </div>
                      <VariableSelector
                        type="color"
                        onSelectVariable={(value) =>
                          handleUpdate({
                            style: { ...resolvedStyle, backgroundColor: value },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
              {/* Backdrop Filters */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Backdrop Filters</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Filter Type Selection */}
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${resolvedStyle.backdropFilter ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                      onClick={() => {
                        if (resolvedStyle.backdropFilter) {
                          handleUpdate({
                            style: { ...resolvedStyle, backdropFilter: undefined },
                          });
                          setLocalBackdropFilterType("blur");
                          setLocalBackdropFilterValue("0");
                          setBackdropFilterUnit("px");
                        }
                      }}
                    >
                      Filter Type
                    </Label>
                    <Select
                      value={localBackdropFilterType}
                      onValueChange={(value) => {
                        const filterInfo = getFilterTypeInfo(value);
                        setLocalBackdropFilterType(value);
                        setLocalBackdropFilterValue(filterInfo.defaultValue);
                        setBackdropFilterUnit(filterInfo.unit as "px" | "%" | "deg" | "");

                        // Apply the change immediately with new default value
                        const filterValue = `${value}(${filterInfo.defaultValue}${filterInfo.unit})`;
                        handleUpdate({
                          style: { ...resolvedStyle, backdropFilter: filterValue },
                        });
                      }}
                    >
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-6 text-xs">
                        <SelectValue placeholder="Select filter type" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 border-gray-600">
                        <SelectItem value="blur" className="text-white text-xs">
                          blur
                        </SelectItem>
                        <SelectItem value="brightness" className="text-white text-xs">
                          brightness
                        </SelectItem>
                        <SelectItem value="contrast" className="text-white text-xs">
                          contrast
                        </SelectItem>

                        <SelectItem value="grayscale" className="text-white text-xs">
                          grayscale
                        </SelectItem>
                        <SelectItem value="hue-rotate" className="text-white text-xs">
                          hue-rotate
                        </SelectItem>
                        <SelectItem value="invert" className="text-white text-xs">
                          invert
                        </SelectItem>

                        <SelectItem value="saturate" className="text-white text-xs">
                          saturate
                        </SelectItem>
                        <SelectItem value="sepia" className="text-white text-xs">
                          sepia
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Filter Value Input */}
                  <div className="space-y-1">
                    <Label
                      className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${(() => {
                        if (!resolvedStyle.backdropFilter) return "text-gray-400";
                        const filterInfo = getFilterTypeInfo(localBackdropFilterType);
                        const isDefaultValue =
                          localBackdropFilterValue === filterInfo.defaultValue &&
                          backdropFilterUnit === filterInfo.unit;
                        return isDefaultValue ? "text-gray-400" : "text-blue-400 bg-blue-400/20";
                      })()}`}
                      onClick={(e) => {
                        // Ignore Command+Click (metaKey)
                        if (e.metaKey) {
                          return;
                        }

                        if (e.altKey || e.metaKey) {
                          // Option+Click: reset to default for current filter type
                          const filterInfo = getFilterTypeInfo(localBackdropFilterType);
                          setLocalBackdropFilterValue(filterInfo.defaultValue);
                          setBackdropFilterUnit(filterInfo.unit as "px" | "%" | "deg" | "");

                          const filterValue = `${localBackdropFilterType}(${filterInfo.defaultValue}${filterInfo.unit})`;
                          handleUpdate({
                            style: { ...resolvedStyle, backdropFilter: filterValue },
                          });
                        } else {
                          // Normal click: reset to original value
                          setLocalBackdropFilterValue(originalBackdropFilterValue);
                          setBackdropFilterUnit(originalBackdropFilterUnit);

                          const filterValue = `${localBackdropFilterType}(${originalBackdropFilterValue}${originalBackdropFilterUnit})`;
                          handleUpdate({
                            style: { ...resolvedStyle, backdropFilter: filterValue },
                          });
                        }
                      }}
                    >
                      Filter Value
                    </Label>

                    <div className="relative">
                      <Input
                        variant="compact-wide"
                        type="number"
                        value={localBackdropFilterValue}
                        onChange={(e) => setLocalBackdropFilterValue(e.target.value)}
                        min={getFilterTypeInfo(localBackdropFilterType).min}
                        max={getFilterTypeInfo(localBackdropFilterType).max}
                        step={getFilterTypeInfo(localBackdropFilterType).step}
                        onFocus={() => {
                          // Store original value for reset on blur
                          const currentValue = resolvedStyle.backdropFilter;
                          if (currentValue) {
                            const match = currentValue.match(/\(([^)]+)\)/);
                            if (match) {
                              const valueWithUnit = match[1];
                              const numericValue = valueWithUnit.replace(/[^\d.]/g, "");
                              const unit = valueWithUnit.replace(/[\d.]/g, "");
                              setLocalBackdropFilterValue(numericValue);
                              setBackdropFilterUnit(unit as "px" | "%" | "deg" | "");
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // Apply the change immediately
                            const filterValue = `${localBackdropFilterType}(${localBackdropFilterValue}${backdropFilterUnit})`;
                            handleUpdate({
                              style: { ...resolvedStyle, backdropFilter: filterValue },
                            });
                            // Select all text for easy editing
                            e.currentTarget.select();
                          } else if (e.key === "Escape") {
                            // Reset to original value
                            const currentValue = resolvedStyle.backdropFilter;
                            if (currentValue) {
                              const match = currentValue.match(/\(([^)]+)\)/);
                              if (match) {
                                const valueWithUnit = match[1];
                                const numericValue = valueWithUnit.replace(/[^\d.]/g, "");
                                const unit = valueWithUnit.replace(/[\d.]/g, "");
                                setLocalBackdropFilterValue(numericValue);
                                setLocalBackdropFilterType(
                                  resolvedStyle.backdropFilter.match(
                                    /^(\w+(?:-\w+)?)/,
                                  )?.[1] || "blur",
                                );
                                setBackdropFilterUnit(unit as "px" | "%" | "deg" | "");
                              }
                            }
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={() => {
                          // Apply the change
                          const filterValue = `${localBackdropFilterType}(${localBackdropFilterValue}${backdropFilterUnit})`;
                          handleUpdate({
                            style: { ...resolvedStyle, backdropFilter: filterValue },
                          });
                        }}
                        className="bg-gray-700 border-gray-600 text-white pr-12"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => {
                            const filterInfo = getFilterTypeInfo(localBackdropFilterType);
                            // Cycle through available units for this filter type
                            let newUnit = backdropFilterUnit;
                            if (filterInfo.unit === "px") {
                              newUnit = backdropFilterUnit === "px" ? "%" : "px";
                            } else if (filterInfo.unit === "%") {
                              newUnit = backdropFilterUnit === "%" ? "" : "%";
                            } else if (filterInfo.unit === "deg") {
                              newUnit = backdropFilterUnit === "deg" ? "" : "deg";
                            } else {
                              newUnit =
                                backdropFilterUnit === ""
                                  ? (filterInfo.unit as "" | "px" | "%" | "deg")
                                  : "";
                            }

                            setBackdropFilterUnit(newUnit);
                            const filterValue = `${localBackdropFilterType}(${localBackdropFilterValue}${newUnit})`;
                            handleUpdate({
                              style: { ...resolvedStyle, backdropFilter: filterValue },
                            });
                          }}
                          className="text-xs text-gray-400 hover:text-white p-0.5 rounded hover:bg-gray-600 transition-colors"
                        >
                          {backdropFilterUnit}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Description */}
                  {resolvedStyle.backdropFilter && (
                    <div className="col-span-2">
                      <div className="text-xs text-gray-400 bg-gray-800/50 p-2 rounded border border-gray-700">
                        <span className="text-gray-400 font-medium">
                          {localBackdropFilterType}:
                        </span>{" "}
                        {getFilterDescription(localBackdropFilterType)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
              {/* Borders */}
              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Borders</h3>

                <div className="grid grid-cols-2 gap-2">
                  {/* Border Style */}
                  <div className="space-y-1">
                    <Label className="text-xs p-0.5 rounded mb-1 text-gray-300">Style</Label>
                  </div>
                  <div className="space-y-1">
                    <div className="flex h-6 border border-gray-600 rounded overflow-hidden">
                      <button
                        type="button"
                        onClick={(e) => {
                          if (e.altKey || e.metaKey) {
                            // Option+Click: reset to default state (solid)
                            setLocalBorderStyle("solid");
                            setIsDefaultBorderStyle(true);
                            handleUpdate({
                              style: { ...resolvedStyle, borderStyle: "solid" },
                            });
                          } else {
                            // Normal click: set to solid
                            setLocalBorderStyle("solid");
                            setIsDefaultBorderStyle(false); // User actively selected solid
                            handleUpdate({
                              style: { ...resolvedStyle, borderStyle: "solid" },
                            });
                          }
                        }}
                        className={`flex-1 flex items-center justify-center border-r border-gray-600 ${
                          localBorderStyle === "solid"
                            ? "bg-gray-500 text-white"
                            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                        }`}
                        title="Solid Border (Option+Click to reset to default)"
                      >
                        ━
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          if (e.altKey || e.metaKey) {
                            // Option+Click: reset to default state (solid)
                            setLocalBorderStyle("solid");
                            setIsDefaultBorderStyle(true);
                            handleUpdate({
                              style: { ...resolvedStyle, borderStyle: "solid" },
                            });
                          } else {
                            // Normal click: set to dashed
                            setLocalBorderStyle("dashed");
                            setIsDefaultBorderStyle(false); // User actively selected dashed
                            handleUpdate({
                              style: { ...resolvedStyle, borderStyle: "dashed" },
                            });
                          }
                        }}
                        className={`flex-1 flex items-center justify-center border-r border-gray-600 ${
                          localBorderStyle === "dashed"
                            ? "bg-gray-500 text-white"
                            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                        }`}
                        title="Dashed Border (Option+Click to reset to default)"
                      >
                        ┅
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          if (e.altKey || e.metaKey) {
                            // Option+Click: reset to default state (solid)
                            setLocalBorderStyle("solid");
                            setIsDefaultBorderStyle(true);
                            handleUpdate({
                              style: { ...resolvedStyle, borderStyle: "solid" },
                            });
                          } else {
                            // Normal click: set to dotted
                            setLocalBorderStyle("dotted");
                            setIsDefaultBorderStyle(false); // User actively selected dotted
                            handleUpdate({
                              style: { ...resolvedStyle, borderStyle: "dotted" },
                            });
                          }
                        }}
                        className={`flex-1 flex items-center justify-center ${
                          localBorderStyle === "dotted"
                            ? "bg-gray-500 text-white"
                            : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                        }`}
                        title="Dot Styled Border (Option+Click to reset to default)"
                      >
                        ⋯
                      </button>
                    </div>
                  </div>

                  {/* Border Color */}
                  <div className="space-y-1">
                    <Label className="text-xs p-0.5 rounded mb-1 text-gray-300">Color</Label>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <ColorPicker
                          value={resolvedStyle.borderColor || "#000000"}
                          onChange={(color) => {
                            setLocalBorderColor(color);
                            // Auto-change border style to solid when setting color
                            if (localBorderStyle === "none") {
                              setLocalBorderStyle("solid");
                              setIsDefaultBorderStyle(false);
                            }
                            handleUpdate({
                              style: {
                                ...resolvedStyle,
                                borderColor: color,
                                borderStyle:
                                  localBorderStyle === "none" ? "solid" : localBorderStyle,
                              },
                            });
                          }}
                          label=""
                          variant="compact"
                          hideIcon={true}
                          onReset={() => {
                            setLocalBorderColor("#000000");
                            handleUpdate({
                              style: { ...resolvedStyle, borderColor: "#000000" },
                            });
                          }}
                        />
                      </div>
                      <VariableSelector
                        type="color"
                        onSelectVariable={(value) => {
                          setLocalBorderColor(value);
                          // Auto-change border style to solid when setting color
                          if (localBorderStyle === "none") {
                            setLocalBorderStyle("solid");
                            setIsDefaultBorderStyle(false);
                          }
                          handleUpdate({
                            style: {
                              ...resolvedStyle,
                              borderColor: value,
                              borderStyle: localBorderStyle === "none" ? "solid" : localBorderStyle,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* Border Width */}
                  {!showBorderWidthFields && (
                    <>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${
                            resolvedStyle.borderWidth &&
                            resolvedStyle.borderWidth !== 1
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-300"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderWidth("1");
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderWidth: 1,
                                  borderTopWidth: 1,
                                  borderRightWidth: 1,
                                  borderBottomWidth: 1,
                                  borderLeftWidth: 1,
                                },
                              });
                            }
                          }}
                        >
                          Width
                        </Label>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="relative flex-1">
                            <Input
                              variant="compact-wide"
                              type="number"
                              min="0"
                              step="1"
                              value={localBorderWidth}
                              placeholder="1"
                              onChange={(e) => setLocalBorderWidth(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  // Call blur logic directly without blurring the field
                                  const parsed = parseInt(e.currentTarget.value, 10);
                                  const value =
                                    e.currentTarget.value === ""
                                      ? 0
                                      : Number.isNaN(parsed)
                                        ? 0
                                        : parsed;
                                  setLocalBorderWidth(value.toString());
                                  // Auto-change border style to solid when setting width
                                  if (localBorderStyle === "none") {
                                    setLocalBorderStyle("solid");
                                    setIsDefaultBorderStyle(false);
                                  }
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      borderWidth: value,
                                      borderStyle:
                                        localBorderStyle === "none" ? "solid" : localBorderStyle,
                                      // Update all individual border widths to match the main width
                                      borderTopWidth: value,
                                      borderRightWidth: value,
                                      borderBottomWidth: value,
                                      borderLeftWidth: value,
                                    },
                                  });
                                  // Select all text for easy editing
                                  e.currentTarget.select();
                                }
                              }}
                              onBlur={() => {
                                const parsed = parseInt(localBorderWidth, 10);
                                const value =
                                  localBorderWidth === ""
                                    ? 0
                                    : Number.isNaN(parsed)
                                      ? 0
                                      : parsed;
                                setLocalBorderWidth(value.toString());
                                // Auto-change border style to solid when setting width
                                if (localBorderStyle === "none") {
                                  setLocalBorderStyle("solid");
                                  setIsDefaultBorderStyle(false);
                                }
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    borderWidth: value,
                                    borderStyle:
                                      localBorderStyle === "none" ? "solid" : localBorderStyle,
                                    // Update all individual border widths to match the main width
                                    borderTopWidth: value,
                                    borderRightWidth: value,
                                    borderBottomWidth: value,
                                    borderLeftWidth: value,
                                  },
                                });
                              }}
                              className="bg-gray-700 border-gray-600 text-white pr-12"
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                              <span className="text-xs text-gray-400">px</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowBorderWidthFields(!showBorderWidthFields)}
                            className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                            title="Toggle individual border width fields"
                          >
                            ▢
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Individual Border Width Fields */}
                  {showBorderWidthFields && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-300">Width</Label>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => setShowBorderWidthFields(!showBorderWidthFields)}
                            className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                            title="Toggle back to main width field"
                          >
                            ▢
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderLeftWidth &&
                            resolvedStyle.borderLeftWidth !== 1
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderLeftWidth("1");
                              handleUpdate({
                                style: { ...resolvedStyle, borderLeftWidth: 1 },
                              });
                            }
                          }}
                        >
                          ← Left
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderLeftWidth}
                            placeholder="0"
                            onChange={(e) => setLocalBorderLeftWidth(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderLeftWidth(value.toString());
                              // Auto-change border style to solid when setting width
                              if (localBorderStyle === "none") {
                                setLocalBorderStyle("solid");
                                setIsDefaultBorderStyle(false);
                              }
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderLeftWidth: value,
                                  borderStyle:
                                    localBorderStyle === "none" ? "solid" : localBorderStyle,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderRightWidth &&
                            resolvedStyle.borderRightWidth !== 1
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderRightWidth("1");
                              handleUpdate({
                                style: { ...resolvedStyle, borderRightWidth: 1 },
                              });
                            }
                          }}
                        >
                          → Right
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderRightWidth}
                            placeholder="0"
                            onChange={(e) => setLocalBorderRightWidth(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderRightWidth(value.toString());
                              // Auto-change border style to solid when setting width
                              if (localBorderStyle === "none") {
                                setLocalBorderStyle("solid");
                                setIsDefaultBorderStyle(false);
                              }
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderRightWidth: value,
                                  borderStyle:
                                    localBorderStyle === "none" ? "solid" : localBorderStyle,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderTopWidth &&
                            resolvedStyle.borderTopWidth !== 1
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderTopWidth("1");
                              handleUpdate({
                                style: { ...resolvedStyle, borderTopWidth: 1 },
                              });
                            }
                          }}
                        >
                          ↑ Top
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderTopWidth}
                            placeholder="0"
                            onChange={(e) => setLocalBorderTopWidth(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderTopWidth(value.toString());
                              // Auto-change border style to solid when setting width
                              if (localBorderStyle === "none") {
                                setLocalBorderStyle("solid");
                                setIsDefaultBorderStyle(false);
                              }
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderTopWidth: value,
                                  borderStyle:
                                    localBorderStyle === "none" ? "solid" : localBorderStyle,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderBottomWidth &&
                            resolvedStyle.borderBottomWidth !== 1
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderBottomWidth("1");
                              handleUpdate({
                                style: { ...resolvedStyle, borderBottomWidth: 1 },
                              });
                            }
                          }}
                        >
                          ↓ Bottom
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderBottomWidth}
                            placeholder="0"
                            onChange={(e) => setLocalBorderBottomWidth(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderBottomWidth(value.toString());
                              // Auto-change border style to solid when setting width
                              if (localBorderStyle === "none") {
                                setLocalBorderStyle("solid");
                                setIsDefaultBorderStyle(false);
                              }
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderBottomWidth: value,
                                  borderStyle:
                                    localBorderStyle === "none" ? "solid" : localBorderStyle,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Border Radius */}
                  {!showBorderRadiusFields && (
                    <>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${
                            resolvedStyle.borderRadius &&
                            resolvedStyle.borderRadius !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-300"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderRadius("");
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderRadius: 0,
                                  borderTopLeftRadius: 0,
                                  borderTopRightRadius: 0,
                                  borderBottomLeftRadius: 0,
                                  borderBottomRightRadius: 0,
                                },
                              });
                            }
                          }}
                        >
                          Radius
                        </Label>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="relative flex-1">
                            <Input
                              variant="compact-wide"
                              type="number"
                              min="0"
                              step="1"
                              value={localBorderRadius}
                              placeholder="0"
                              onChange={(e) => setLocalBorderRadius(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  // Call blur logic directly without blurring the field
                                  const value =
                                    e.currentTarget.value === ""
                                      ? 0
                                      : parseInt(e.currentTarget.value, 10) || 0;
                                  setLocalBorderRadius(value === 0 ? "" : value.toString());
                                  handleUpdate({
                                    style: {
                                      ...resolvedStyle,
                                      borderRadius: value,
                                      // Update all individual border radius values to match the main radius
                                      borderTopLeftRadius: value,
                                      borderTopRightRadius: value,
                                      borderBottomLeftRadius: value,
                                      borderBottomRightRadius: value,
                                    },
                                  });
                                  // Select all text for easy editing
                                  e.currentTarget.select();
                                }
                              }}
                              onBlur={() => {
                                const value =
                                  localBorderRadius === ""
                                    ? 0
                                    : parseInt(localBorderRadius, 10) || 0;
                                setLocalBorderRadius(value === 0 ? "" : value.toString());
                                handleUpdate({
                                  style: {
                                    ...resolvedStyle,
                                    borderRadius: value,
                                    // Update all individual border radius values to match the main radius
                                    borderTopLeftRadius: value,
                                    borderTopRightRadius: value,
                                    borderBottomLeftRadius: value,
                                    borderBottomRightRadius: value,
                                  },
                                });
                              }}
                              className="bg-gray-700 border-gray-600 text-white pr-12"
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                              <span className="text-xs text-gray-400">px</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowBorderRadiusFields(!showBorderRadiusFields)}
                            className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                            title="Toggle individual border radius fields"
                          >
                            ▢
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Individual Border Radius Fields */}
                  {showBorderRadiusFields && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs p-0.5 rounded mb-1 text-gray-300">Radius</Label>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => setShowBorderRadiusFields(!showBorderRadiusFields)}
                            className="w-6 h-6 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                            title="Toggle back to main radius field"
                          >
                            ▢
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderTopLeftRadius &&
                            resolvedStyle.borderTopLeftRadius !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderTopLeftRadius("0");
                              handleUpdate({
                                style: { ...resolvedStyle, borderTopLeftRadius: 0 },
                              });
                            }
                          }}
                        >
                          ↖ Top-Left
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderTopLeftRadius}
                            placeholder="0"
                            onChange={(e) => setLocalBorderTopLeftRadius(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderTopLeftRadius(value.toString());
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderTopLeftRadius: value,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderTopRightRadius &&
                            resolvedStyle.borderTopRightRadius !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderTopRightRadius("0");
                              handleUpdate({
                                style: { ...resolvedStyle, borderTopRightRadius: 0 },
                              });
                            }
                          }}
                        >
                          ↗ Top-Right
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderTopRightRadius}
                            placeholder="0"
                            onChange={(e) => setLocalBorderTopRightRadius(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderTopRightRadius(value.toString());
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderTopRightRadius: value,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderBottomLeftRadius &&
                            resolvedStyle.borderBottomLeftRadius !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderBottomLeftRadius("0");
                              handleUpdate({
                                style: { ...resolvedStyle, borderBottomLeftRadius: 0 },
                              });
                            }
                          }}
                        >
                          ↙ Bottom-Left
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderBottomLeftRadius}
                            placeholder="0"
                            onChange={(e) => setLocalBorderBottomLeftRadius(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderBottomLeftRadius(value.toString());
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderBottomLeftRadius: value,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 text-gray-400 cursor-pointer ${
                            resolvedStyle.borderBottomRightRadius &&
                            resolvedStyle.borderBottomRightRadius !== 0
                              ? "text-blue-400 bg-blue-400/20"
                              : "text-gray-400"
                          }`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              e.preventDefault();
                              setLocalBorderBottomRightRadius("0");
                              handleUpdate({
                                style: { ...resolvedStyle, borderBottomRightRadius: 0 },
                              });
                            }
                          }}
                        >
                          ↘ Bottom-Right
                        </Label>
                        <div className="relative">
                          <Input
                            variant="compact-wide"
                            type="number"
                            min="0"
                            step="1"
                            value={localBorderBottomRightRadius}
                            placeholder="0"
                            onChange={(e) => setLocalBorderBottomRightRadius(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                                // Use requestAnimationFrame to ensure DOM updates complete
                                requestAnimationFrame(() => {
                                  if (e.currentTarget && document.contains(e.currentTarget)) {
                                    e.currentTarget.focus();
                                    e.currentTarget.select();
                                  }
                                });
                              }
                            }}
                            onBlur={(e) => {
                              const value =
                                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                              setLocalBorderBottomRightRadius(value.toString());
                              handleUpdate({
                                style: {
                                  ...resolvedStyle,
                                  borderBottomRightRadius: value,
                                },
                              });
                            }}
                            className="bg-gray-700 border-gray-600 text-white pr-12"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <span className="text-xs text-gray-400">px</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />

            </TabsContent>

            <TabsContent value="settings" className="space-y-3 mt-4">
              {selectedComponent.type === "text" && (
                <>
                  <div className="mt-1">
                    <h3 className="text-xs font-bold text-gray-300 pl-0.5">Text Settings</h3>
                    <div className="space-y-1">
                      <div className="space-y-1">
                        <Label
                          className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${textContentBinding || (selectedComponent.content?.text && selectedComponent.content.text.trim() !== "") ? "text-blue-400 bg-blue-400/20" : "text-gray-400"}`}
                          onClick={(e) => {
                            if (e.altKey || e.metaKey) {
                              if (textContentBinding) {
                                handleTextContentBindingChange(null);
                              } else {
                                handleUpdate({
                                  content: { ...selectedComponent.content, text: "" },
                                });
                              }
                            }
                          }}
                        >
                          Text Content
                        </Label>
                        <div className="relative">
                          <ConsistentTooltip
                            content={
                              textContentBinding
                                ? "This field is bound to CMS data. Alt+click the 'Text Content' label to remove the binding and enable editing."
                                : ""
                            }
                            trigger={
                              <Textarea
                                value={getResolvedTextContent()}
                                onChange={(e) =>
                                  handleUpdate({
                                    content: { ...selectedComponent.content, text: e.target.value },
                                  })
                                }
                                placeholder="Enter text content..."
                                disabled={!!textContentBinding}
                                className={`bg-gray-700 border-gray-600 text-white resize-y min-h-[60px] pr-8 ${textContentBinding ? "opacity-50 cursor-not-allowed" : ""}`}
                                rows={3}
                              />
                            }
                          />
                          <DataBindingButton
                            componentId={selectedComponent.id}
                            componentType={selectedComponent.type}
                            propertyPath="content.text"
                            currentBinding={textContentBinding}
                            onBindingChange={handleTextContentBindingChange}
                          />
                        </div>
                        {(() => {
                          const parentComponent = skinComponents.find(
                            (c) => c.id === selectedComponent.parentId,
                          );
                          if (parentComponent?.type === "collection-list") {
                            return (
                              <div className="mt-2 space-y-1">
                                <Label
                                  className={`text-xs p-0.5 rounded mb-1 cursor-pointer ${selectedComponent.content?.dataBinding && selectedComponent.content.dataBinding !== "none" ? "text-purple-400 bg-purple-400/20" : "text-gray-400"}`}
                                >
                                  Collection Field Binding
                                </Label>
                                <Select
                                  value={selectedComponent.content?.dataBinding || "none"}
                                  onValueChange={(value) =>
                                    handleUpdate({
                                      content: {
                                        ...selectedComponent.content,
                                        dataBinding: value === "none" ? undefined : value,
                                      },
                                    })
                                  }
                                >
                                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white text-xs h-7">
                                    <SelectValue placeholder="Select field to bind..." />
                                  </SelectTrigger>
                                  <SelectContent className="bg-gray-700 border-gray-600">
                                    <SelectItem value="none" className="text-white text-xs">
                                      No binding
                                    </SelectItem>
                                    {collectionFields.map((field) => (
                                      <SelectItem
                                        key={field.name}
                                        value={field.name}
                                        className="text-white text-xs"
                                      >
                                        {field.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-400">
                                  Bind this text to a field from the collection data
                                </p>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                  <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
                </>
              )}

              <div className="mt-1">
                <h3 className="text-xs font-bold text-gray-300 pl-0.5">Component Settings</h3>

                <div className="space-y-1">
                  <div className="flex items-center justify-between h-6 group relative">
                    <Label className="text-white text-xs p-0.5">Mouse Events</Label>
                    <Switch
                      checked={selectedComponent.pointerEvents !== false}
                      onCheckedChange={(enabled) => handleUpdate({ pointerEvents: enabled })}
                      className="h-4 w-8 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                    />
                    <div
                      className="absolute left-0 top-full mt-2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 border border-gray-600"
                      style={{ maxWidth: "300px" }}
                    >
                      When disabled, mouse events pass through to the panorama viewer, allowing
                      camera navigation.
                    </div>
                  </div>

                  <div className="flex items-center justify-between h-6 group relative">
                    <Label className="text-white text-xs p-0.5">Hide on start</Label>
                    <Switch
                      checked={selectedComponent.hideOnStart === true}
                      onCheckedChange={(enabled) => handleUpdate({ hideOnStart: enabled })}
                      className="h-4 w-8 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                    />
                    <div
                      className="absolute left-0 top-full mt-2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 border border-gray-600"
                      style={{ maxWidth: "300px" }}
                    >
                      When enabled, this component starts hidden in preview and export, but remains
                      visible while editing.
                    </div>
                  </div>

                  {selectedComponent.type === "box" && (
                    <div className="flex items-center justify-between h-6 group relative">
                      <Label className="text-white text-xs p-0.5">Esc Key to Hide</Label>
                      <Switch
                        checked={selectedComponent.escToHide === true}
                        onCheckedChange={(enabled) => handleUpdate({ escToHide: enabled })}
                        className="h-4 w-8 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                      />
                      <div
                        className="absolute left-0 top-full mt-2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 border border-gray-600"
                        style={{ maxWidth: "300px" }}
                      >
                        When enabled, pressing the Esc key will hide this box component.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Separator className="bg-gray-600 -ml-3 -mr-12 h-px" />
            </TabsContent>

            {/* Actions Tab */}
            <TabsContent value="actions" className="space-y-4 mt-4" data-skin-ui>
              <ActionsPanel
                actions={readActions(content)}
                panoramas={(panoramas || []) as Array<{ id: string; name: string }>}
                hotspots={hotspots.map((hotspot) => ({ id: hotspot.id, name: hotspot.name }))}
                skinComponents={skinComponents.map((component) => ({
                  id: component.id,
                  name: component.name,
                  parentId: component.parentId,
                }))}
                isActionMenuOpen={isActionMenuOpen}
                setIsActionMenuOpen={setIsActionMenuOpen}
                expandedActionIds={expandedActionIds}
                setExpandedActionIds={setExpandedActionIds}
                hotspotVisibilityConfigs={hotspotVisibilityConfigs}
                setHotspotVisibilityConfigs={setHotspotVisibilityConfigs}
                onAddAction={addAction}
                onRemoveAction={removeAction}
                onUpdateAction={updateAction}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Conditional Visibility Dialog */}
      {showConditionalVisibility && (
        <ConditionalVisibilityDialog
          isOpen={showConditionalVisibilityDialog}
          onClose={() => setShowConditionalVisibilityDialog(false)}
          onSave={handleConditionalVisibilityRulesChange}
          initialRules={conditionalVisibilityRules}
        />
      )}
    </div>
  );
};
